import React, { useEffect, useLayoutEffect, useState, useRef, useMemo } from "react";
import axios from "axios";
import YouTube from "react-youtube";
import "./App.scss";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Slider from "react-slick";
import { useInView } from "react-intersection-observer";
import { Routes, Route, Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import ContactPage from "./pages/ContactPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";  
import YouTubeCard from "./YouTubeCard/YouTubeCard";
import doubleTapIcon from "./img/double-tap.png";
import { API_BASE_URL } from "./apiConfig";

export const AppContext = React.createContext();

// These interaction-only panels are large and not needed for first paint. Keep
// them out of the initial homepage bundle; CRA/Webpack will emit separate chunks.
const MediaAnalysisModal = React.lazy(() => import("./MediaAnalysisModal"));
const CommentsModal = React.lazy(() => import("./CommentsModal"));

function ModalChunkFallback({ label = "Opening…" }) {
  return (
    <div className="modal-chunk-loading-backdrop" role="status" aria-live="polite">
      <div className="modal-chunk-loading-card">{label}</div>
    </div>
  );
}


function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

class AnalysisErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error("[AI Analysis] Render error", error, info);
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="analysis-render-error-backdrop" role="dialog" aria-modal="true" aria-label="AI Analysis error">
        <div className="analysis-render-error-card">
          <h2>AI Analysis couldn’t load</h2>
          <p>Something went wrong while rendering this analysis. Close this window and try the headline again.</p>
          <button type="button" onClick={this.props.onClose}>Close</button>
        </div>
      </div>
    );
  }
}

function useIsMobileScreen(breakpoint) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false,
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleResize = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [breakpoint]);

  return isMobile;
}

function isAppleMobileDevice() {
  if (typeof window === "undefined") return false;
  const { userAgent, platform, maxTouchPoints } = window.navigator;
  return /iPad|iPhone|iPod/.test(userAgent) || (platform === "MacIntel" && maxTouchPoints > 1);
}

const HEADLINE_MOBILE_BREAKPOINT = 800;

function measureTextLineWidths(textEl) {
  if (!textEl || typeof window === "undefined") {
    return {
      availableWidth: 0,
      lineWidths: [],
    };
  }

  const styles = window.getComputedStyle(textEl);
  const paddingLeft = parseFloat(styles.paddingLeft) || 0;
  const paddingRight = parseFloat(styles.paddingRight) || 0;
  const availableWidth = Math.max(1, textEl.clientWidth - paddingLeft - paddingRight);
  const lineWidths = [];

  try {
    const range = document.createRange();
    range.selectNodeContents(textEl);
    const rects = Array.from(range.getClientRects()).filter(
      (r) => r.width > 0 && r.height > 0,
    );

    if (rects.length > 0) {
      const sorted = [...rects].sort((a, b) => a.top - b.top || a.left - b.left);
      const tolerance = Math.max(8, sorted[0].height * 0.22);
      let lastTop = -Infinity;

      for (const rect of sorted) {
        if (lineWidths.length === 0 || Math.abs(rect.top - lastTop) > tolerance) {
          lineWidths.push(rect.width);
          lastTop = rect.top;
          continue;
        }

        lineWidths[lineWidths.length - 1] += rect.width;
      }
    }
  } catch {
    // ignore
  }

  return {
    availableWidth,
    lineWidths,
  };
}

/**
 * Text-only line count for the hero headline (excludes engagement icon).
 * Range.getClientRects() tracks wrapped lines; scrollHeight is the fallback.
 */
function vipHeroDesktopLineCount(textEl) {
  if (!textEl) return 1;

  const { lineWidths } = measureTextLineWidths(textEl);
  const rectLines = lineWidths.length;

  const styles = window.getComputedStyle(textEl);
  const fontSize = parseFloat(styles.fontSize) || 0;
  const rawLineHeight = parseFloat(styles.lineHeight);
  const lineHeight = Number.isFinite(rawLineHeight)
    ? rawLineHeight
    : fontSize * 1.4;
  const contentHeight = Math.max(0, textEl.scrollHeight);
  const scrollLines = Math.max(1, Math.ceil((contentHeight - 1) / lineHeight));

  return Math.max(1, rectLines, scrollLines);
}

const VIP_HEADLINE_ROW_GAP = 10;
const MOBILE_HERO_CONTROLS_HIDE_DELAY_MS = 3800;
const MOBILE_HERO_AUTOPREVIEW_START_DELAY_MS = 1500;
const MOBILE_HERO_AUTOPREVIEW_DURATION_MS = 4500;
const HOMEPAGE_AI_HINT_TYPE_MS = 1200;
const HOMEPAGE_AI_HINT_REVERSE_START_MS = 5 * 1000;
const HOMEPAGE_AI_HINT_DELETE_MS = 1000;
const HOMEPAGE_AI_HINT_HOLD_MS = Math.max(
  0,
  HOMEPAGE_AI_HINT_REVERSE_START_MS - HOMEPAGE_AI_HINT_TYPE_MS,
);
const HOMEPAGE_AI_HINT_TOTAL_MS =
  HOMEPAGE_AI_HINT_TYPE_MS + HOMEPAGE_AI_HINT_HOLD_MS + HOMEPAGE_AI_HINT_DELETE_MS;
const HOMEPAGE_AI_HINT_ACCENT_TEXT = "Tap Headlines";
const HOMEPAGE_AI_HINT_FULL_TEXT = `${HOMEPAGE_AI_HINT_ACCENT_TEXT} for AI Analysis`;

/** Desktop VIP hero headline `top` (px) from measured line count (2–5 lines). */
function vipDesktopHeroTopPxFromLines(lines) {
  const n = Math.min(5, Math.max(1, Math.round(lines)));
  if (n <= 2) return -53;
  if (n === 3) return -103;
  if (n === 4) return -156;
  return -208;
}

/**
 * Position the headline so its bottom sits VIP_HEADLINE_ROW_GAP above the first YouTube row.
 * Uses measured box height when available; falls back to line-count buckets.
 */
function vipDesktopHeroTopPx(titleEl, textEl, anchorEl) {
  const height = Math.round(titleEl?.getBoundingClientRect().height ?? 0);
  const lines = Math.min(5, vipHeroDesktopLineCount(textEl));

  if (height > 0 && anchorEl) {
    const raw = window.getComputedStyle(anchorEl).getPropertyValue("--vip-first-row-top-padding");
    const anchorBottom = Number.parseFloat(raw) || 84;
    let topPx = Math.round(anchorBottom - VIP_HEADLINE_ROW_GAP - height);
    if (lines <= 2) {
      topPx = Math.max(topPx, vipDesktopHeroTopPxFromLines(lines));
    }
    return topPx;
  }

  return vipDesktopHeroTopPxFromLines(lines);
}

function AdaptiveHeadline({
  as: Tag = "h4",
  className = "",
  desktopMaxLines = 1,
  mobileMaxLines = 2,
  desktopMinScale = 0.65,
  mobileMinScale = 0.82,
  children,
  ...rest
}) {
  const textRef = useRef(null);

  useLayoutEffect(() => {
    const element = textRef.current;
    if (!element || typeof window === "undefined") return;

    let frameId = 0;
    let resizeObserver;

    const getContentMetrics = () => {
      const styles = window.getComputedStyle(element);
      const fontSize = parseFloat(styles.fontSize) || 0;
      const rawLineHeight = parseFloat(styles.lineHeight);
      const lineHeight = Number.isFinite(rawLineHeight)
        ? rawLineHeight
        : fontSize * 1.2;
      const paddingTop = parseFloat(styles.paddingTop) || 0;
      const paddingBottom = parseFloat(styles.paddingBottom) || 0;
      const contentHeight = Math.max(
        0,
        element.scrollHeight - paddingTop - paddingBottom,
      );

      return { lineHeight, contentHeight };
    };

    const exceedsMaxLines = (maxLines) => {
      const { lineHeight, contentHeight } = getContentMetrics();
      return contentHeight > lineHeight * maxLines + 1;
    };

    const getMobileFitScore = () => {
      const { availableWidth, lineWidths } = measureTextLineWidths(element);
      if (availableWidth <= 0 || lineWidths.length === 0) {
        return {
          lineCount: 1,
          score: 0,
        };
      }

      const fillRatios = lineWidths.map((width) => Math.min(1, width / availableWidth));
      const leadingLineRatios = fillRatios.slice(0, -1);
      const leadingLineFill = leadingLineRatios.length > 0
        ? Math.min(...leadingLineRatios)
        : fillRatios[0];
      const averageFill = fillRatios.reduce((sum, ratio) => sum + ratio, 0) / fillRatios.length;

      return {
        lineCount: lineWidths.length,
        score: leadingLineFill * 1000 + averageFill,
      };
    };

    const fitHeadline = () => {
      element.style.removeProperty("font-size");
      const styles = window.getComputedStyle(element);
      const baseFontSize = parseFloat(styles.fontSize);
      const isMobile = window.innerWidth <= HEADLINE_MOBILE_BREAKPOINT;
      const maxLines = isMobile ? mobileMaxLines : desktopMaxLines;
      const minFontSize = baseFontSize * (isMobile ? mobileMinScale : desktopMinScale);
      const fontStep = isMobile ? 0.5 : 0.25;

      let nextFontSize = baseFontSize;
      element.style.setProperty("font-size", `${baseFontSize}px`, "important");
      element.style.removeProperty("-webkit-line-clamp");

      while (nextFontSize > minFontSize && exceedsMaxLines(maxLines)) {
        nextFontSize = Math.max(minFontSize, nextFontSize - fontStep);
        element.style.setProperty("font-size", `${nextFontSize}px`, "important");
      }

      if (isMobile) {
        let bestFit = null;
        let candidateFontSize = nextFontSize;

        const captureBestFit = (fontSize) => {
          const { lineCount, score } = getMobileFitScore();
          if (lineCount > maxLines) return;

          if (
            !bestFit ||
            score > bestFit.score + 0.001 ||
            (Math.abs(score - bestFit.score) <= 0.001 && fontSize > bestFit.fontSize)
          ) {
            bestFit = { fontSize, score };
          }
        };

        captureBestFit(candidateFontSize);

        while (candidateFontSize > minFontSize) {
          candidateFontSize = Math.max(minFontSize, candidateFontSize - fontStep);
          element.style.setProperty("font-size", `${candidateFontSize}px`, "important");
          captureBestFit(candidateFontSize);
        }

        if (bestFit) {
          nextFontSize = bestFit.fontSize;
          element.style.setProperty("font-size", `${nextFontSize}px`, "important");
        }
      }

      element.style.setProperty("-webkit-line-clamp", `${maxLines}`);
    };

    const scheduleFit = () => {
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(fitHeadline);
    };

    scheduleFit();
    window.addEventListener("resize", scheduleFit);

    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(scheduleFit);
      resizeObserver.observe(element);
      if (element.parentElement) resizeObserver.observe(element.parentElement);
    }

    const fontsReady = document.fonts?.ready;
    if (fontsReady) {
      fontsReady.then(scheduleFit).catch(() => {});
    }

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", scheduleFit);
      resizeObserver?.disconnect();
    };
  }, [children, desktopMaxLines, mobileMaxLines, desktopMinScale, mobileMinScale]);

  return <Tag ref={textRef} className={["adaptive-headline", className].filter(Boolean).join(" ")} {...rest}>{children}</Tag>;
}

const CountryDropdown = ({ selectedCountry, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selected = countries.find((c) => c.code === selectedCountry);

  const handleSelect = (code) => {
    onChange({ target: { value: code } });
    setIsOpen(false);
  };

  return (
    <div className="custom-dropdown">
      <button
        className="custom-dropdown__toggle"
        onClick={() => setIsOpen(!isOpen)}
      >
        {selected?.name || "Select Country"}
      </button>
    </div>
  );
};

const LazyYouTubeEmbed = ({ videoId, thumbnail }) => {
  const { ref, inView } = useInView({ triggerOnce: true });
  const containerRef = useRef(null);
  const iframeRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            try {
              if (iframeRef.current && iframeRef.current.contentWindow) {
                iframeRef.current.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo' }), '*');
              }
            } catch {}
          }
        });
      },
      { threshold: 0 }
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={(node) => {
        ref(node);
        containerRef.current = node;
      }}
      className="position-relative"
      style={{
        width: "100%",
        paddingBottom: "56.25%", // 16:9 aspect ratio
        backgroundColor: "#000",
        borderRadius: "10px",
        overflow: "hidden",
      }}
    >
      {inView ? (
        <iframe
          ref={iframeRef}
          src={`https://www.youtube.com/embed/${videoId}?enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`}
          title="YouTube video"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
          }}
        />
      ) : (
        <img
          src={thumbnail}
          alt="Video thumbnail"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      )}
    </div>
  );
};

const LazyClickableYouTubeEmbed = ({ videoId, thumbnail }) => {
  const [showPlayer, setShowPlayer] = useState(false);
  const containerRef = useRef(null);
  const iframeRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            try {
              if (iframeRef.current && iframeRef.current.contentWindow) {
                iframeRef.current.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo' }), '*');
              }
            } catch {}
          }
        });
      },
      { threshold: 0 }
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const handleClick = () => {
    setShowPlayer(true);
  };

  return (
    <div
      ref={containerRef}
      onClick={handleClick}
      style={{
        width: "100%",
        paddingBottom: "56.25%", // 16:9
        position: "relative",
        backgroundColor: "#000",
        cursor: "pointer",
        borderRadius: "10px",
        overflow: "hidden",
      }}
    >
      {showPlayer ? (
        <iframe
          ref={iframeRef}
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=0&enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`}
          title="YouTube video"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
          }}
        />
      ) : (
        <>
          <img
            src={thumbnail}
            alt="Video thumbnail"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              filter: "brightness(0.8)",
            }}
          />
          <div className="play-btn" aria-hidden="true">
            <span className="play-glyph" />
          </div>
        </>
      )}
    </div>
  );
};

// Controlled YouTube embed that auto-plays and notifies on pause/end
const SmartYouTubeEmbed = ({
  videoId,
  thumbnail,
  isActive,
  onRequestPlay,
  onRequestStop,
  onPlaying,
  onPausedOrEnded,
}) => {
  const isControlled = typeof isActive === "boolean";
  const [localActive, setLocalActive] = useState(false);
  const active = isControlled ? isActive : localActive;
  // Store the player instance so we can unmute programmatically
  const playerRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            try {
              const p = playerRef.current;
              if (p && typeof p.pauseVideo === "function") {
                const pausePromise = p.pauseVideo();
                if (pausePromise && pausePromise.catch) pausePromise.catch(() => {});
              }
            } catch {}
          }
        });
      },
      { threshold: 0 }
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);
  const handleClick = () => {
    if (isControlled) {
      if (!active) onRequestPlay?.();
    } else {
      setLocalActive(true);
    }
  };

  const handleStateChange = (e) => {
    const state = e?.data;
    const Y = window.YT?.PlayerState;
    const isNowPlaying = Y ? state === Y.PLAYING : state === 1;
    const isStoped = Y ? (state === Y.PAUSED || state === Y.ENDED) : (state === 2 || state === 0);

    if (isNowPlaying) {
      // Auto-unmute the moment the user presses play — this is a user-gesture
      // context so browsers allow it even under the autoplay policy.
      try {
        const p = playerRef.current;
        if (p && typeof p.unMute === "function") {
          const unmutePromise = p.unMute();
          if (unmutePromise && unmutePromise.catch) unmutePromise.catch(() => {});
          const volPromise = p.setVolume(100);
          if (volPromise && volPromise.catch) volPromise.catch(() => {});
        }
      } catch { }
      onPlaying?.();
    }
    if (isStoped) {
      if (isControlled) onRequestStop?.();
      else setLocalActive(false);
      onPausedOrEnded?.();
    }
  };

  // When controlled and deactivated, pause audio immediately before React removes the player
  useEffect(() => {
    if (isControlled && !isActive) {
      try {
        const p = playerRef.current;
        if (p && typeof p.pauseVideo === "function") {
          const pausePromise = p.pauseVideo();
          if (pausePromise && pausePromise.catch) pausePromise.catch(() => {});
        }
      } catch { }
    }
  }, [isControlled, isActive]);

  const ytOpts = useMemo(
    () => ({
      width: "100%",
      height: "100%",
      playerVars: {
        autoplay: 1,
        mute: 1,
        playsinline: 1,
        rel: 0,
        modestbranding: 1,
        enablejsapi: 1,
      },
    }),
    [],
  );

  return (
    <div
      ref={containerRef}
      onClick={handleClick}
      style={{
        width: "100%",
        paddingBottom: "56.25%",
        position: "static",
        backgroundColor: "#000",
        cursor: active ? "auto" : "pointer",
        borderRadius: "10px",
        overflow: "hidden",
      }}
    >
      {active ? (
        <YouTube
          key={videoId}
          videoId={videoId}
          opts={ytOpts}
          onReady={(e) => {
            try {
              playerRef.current = e?.target;
              const playPromise = e?.target?.playVideo?.();
              if (playPromise && playPromise.catch) playPromise.catch(() => {});
            } catch { }
          }}
          onStateChange={handleStateChange}
          className="yt-fill"
          iframeClassName="yt-iframe"
        />
      ) : (
        <>
          <img
            src={thumbnail}
            alt="Video thumbnail"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              filter: "brightness(0.8)",
            }}
          />
          <div className="play-btn" aria-hidden="true">
            <span className="play-glyph" />
          </div>
        </>
      )}
    </div>
  );
};

/** Animated loading overlay shown while a stream is connecting */
const StreamLoader = () => (
  <div className="live-stream-loader">
    <div className="live-stream-loader__spinner" />
    <div className="live-stream-loader__dot-row">
      <span /><span /><span />
    </div>
    <p className="live-stream-loader__label">Loading stream…</p>
  </div>
);

/** Overlay shown when a stream is blocked by X-Frame-Options */
const StreamBlockedOverlay = ({ url, message, buttonText, onAutoAdvance }) => {
  useEffect(() => {
    if (onAutoAdvance) {
      const timerId = setTimeout(onAutoAdvance, 2000);
      return () => clearTimeout(timerId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  return (
    <div className="live-stream-loader" style={{ textAlign: "center", padding: "20px" }}>
      <p className="live-stream-loader__label" style={{ color: "#fff", marginBottom: "8px" }}>
        {message || "This stream cannot be embedded."}
      </p>
      <a 
        href={url} 
        target="_blank" 
        rel="noopener noreferrer"
        className="btn btn-sm btn-outline-light mt-2"
        style={{ borderColor: "rgba(255,255,255,0.2)", borderRadius: "20px" }}
      >
        {buttonText || "Open in New Tab"} ↗
      </a>
      {onAutoAdvance && (
        <p style={{ color: "#aaa", fontSize: "0.85rem", marginTop: "15px" }}>
          Moving to next link in 2 seconds...
        </p>
      )}
    </div>
  );
};

/**
 * HLS player with native-HLS fallback.
 * Uses hls.js when the browser cannot play an HLS manifest natively.
 */
const HlsVideoPlayer = ({ src, className, onCanPlay, onFatalError }) => {
  const videoRef = useRef(null);
  const fatalErrorRef = useRef(onFatalError);
  fatalErrorRef.current = onFatalError;

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return undefined;

    let hls = null;
    let disposed = false;
    let fatalReported = false;
    let networkRecoveryAttempted = false;
    let mediaRecoveryAttempted = false;

    const reportFatal = (reason) => {
      if (disposed || fatalReported) return;
      fatalReported = true;
      console.warn("[LiveTV] HLS playback failed:", reason, src);
      fatalErrorRef.current?.();
    };

    const tryPlay = () => {
      const promise = video.play?.();
      if (promise?.catch) {
        // Autoplay may be blocked even when the stream itself is healthy.
        // Controls remain available, so autoplay rejection is not a stream error.
        promise.catch(() => {});
      }
    };

    // Safari and some platform players support HLS directly.
    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = src;
      tryPlay();
      return () => {
        disposed = true;
        video.removeAttribute("src");
        video.load();
      };
    }

    // hls.js is only needed when a user actually opens an HLS live stream in a
    // browser without native HLS support. Load it on demand so the relatively
    // large player library is not part of the homepage's initial JS chunk.
    (async () => {
      try {
        const module = await import("hls.js");
        if (disposed) return;
        const Hls = module.default;
        if (!Hls?.isSupported?.()) {
          reportFatal("HLS is not supported by this browser");
          return;
        }

        hls = new Hls({
          enableWorker: true,
          lowLatencyMode: true,
          backBufferLength: 30,
        });
        hls.attachMedia(video);
        hls.on(Hls.Events.MEDIA_ATTACHED, () => {
          if (!disposed) hls.loadSource(src);
        });
        hls.on(Hls.Events.MANIFEST_PARSED, tryPlay);
        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (!data?.fatal || disposed) return;

          // Give hls.js one recovery attempt per recoverable error class. If the
          // recovered stream fails fatally again, move to the next curated URL.
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR && !networkRecoveryAttempted) {
            networkRecoveryAttempted = true;
            try {
              hls.startLoad();
              return;
            } catch {}
          }
          if (data.type === Hls.ErrorTypes.MEDIA_ERROR && !mediaRecoveryAttempted) {
            mediaRecoveryAttempted = true;
            try {
              hls.recoverMediaError();
              return;
            } catch {}
          }
          reportFatal(data.details || data.type || "fatal HLS error");
        });
      } catch (error) {
        reportFatal(error?.message || "Could not load HLS player");
      }
    })();

    return () => {
      disposed = true;
      try { hls?.destroy(); } catch {}
    };
  }, [src]);

  return (
    <video
      ref={videoRef}
      controls
      autoPlay
      playsInline
      className={className}
      onCanPlay={onCanPlay}
      onError={() => fatalErrorRef.current?.()}
    >
      Your browser does not support the video tag.
    </video>
  );
};

/**
 * LiveStreamPlayer
 * Priority: curated YouTube Live → all IPTV fallbacks → additional YouTube fallbacks → official website
 *
 * - YouTube embed/watch URLs  → SmartYouTubeEmbed
 * - YouTube channel /live URLs → Always blocked by YT. Shows StreamBlockedOverlay.
 * - *.m3u8 URLs               → native HLS or hls.js player
 * - Website URLs (col E)      → Calls backend to check X-Frame-Options. If blocked, shows StreamBlockedOverlay.
 */
const LiveStreamPlayer = ({ videoUrls = [], channelKey }) => {
  const [urlIdx, setUrlIdx] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isWebsiteEmbeddable, setIsWebsiteEmbeddable] = useState(null);
  const [streamTimedOut, setStreamTimedOut] = useState(false);

  // Reset everything when the channel changes
  useEffect(() => {
    setUrlIdx(0);
    setIsLoading(true);
    setIsWebsiteEmbeddable(null);
    setStreamTimedOut(false);
  }, [channelKey]);

  // Show loader whenever we switch to a new URL
  useEffect(() => {
    setIsLoading(true);
    setIsWebsiteEmbeddable(null);
    setStreamTimedOut(false);
  }, [urlIdx]);

  const advance = () => {
    setUrlIdx((prev) => {
      const next = prev + 1;
      if (next < videoUrls.length) {
        console.log(`[LiveTV] Advancing ${prev} → ${next}:`, videoUrls[next]);
        return next;
      }
      console.warn("[LiveTV] All URLs exhausted.");
      return prev;
    });
  };

  const onLoaded = () => setIsLoading(false);

  // Derive URL state before the effects so hook order stays stable even when
  // a channel temporarily has no URLs.
  const hasVideoUrls = Array.isArray(videoUrls) && videoUrls.length > 0;
  const url = hasVideoUrls ? (videoUrls[urlIdx] || "") : "";
  const isYouTube = url.includes("youtube.com") || url.includes("youtu.be");
  const isM3U8 = url.endsWith(".m3u8") || url.includes(".m3u8?");
  const isLastUrl = hasVideoUrls && urlIdx >= videoUrls.length - 1;
  const isMixedContent = window.location.protocol === "https:" && url.toLowerCase().startsWith("http://");

  useEffect(() => {
    if (!url || isMixedContent || isYouTube || isM3U8 || !isLastUrl) return;
    let cancelled = false;
    setIsWebsiteEmbeddable(null);
    axios.get(`${API_BASE_URL}/check-iframe?url=${encodeURIComponent(url)}`, { timeout: 8000 })
      .then((res) => {
        if (!cancelled) setIsWebsiteEmbeddable(res.data?.embeddable ?? false);
      })
      .catch(() => {
        if (!cancelled) setIsWebsiteEmbeddable(false);
      });
    return () => { cancelled = true; };
  }, [url, isMixedContent, isYouTube, isM3U8, isLastUrl]);

  // Do not leave users staring at an endless loader when an IPTV URL is dead.
  useEffect(() => {
    if (!isM3U8 || !isLoading || streamTimedOut) return undefined;
    const timer = window.setTimeout(() => {
      if (!isLastUrl) {
        advance();
      } else {
        setStreamTimedOut(true);
        setIsLoading(false);
      }
    }, 12000);
    return () => window.clearTimeout(timer);
  // advance is intentionally omitted; urlIdx/url changes reset the timer.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, isM3U8, isLoading, isLastUrl, streamTimedOut]);

  if (!hasVideoUrls) {
    return <div className="live-player-error">No playable stream found.</div>;
  }

  // HTTPS pages cannot embed or play plain-HTTP streams. Skip immediately
  // when another curated fallback exists instead of waiting for a guaranteed
  // browser mixed-content failure.
  if (isMixedContent) {
    if (!isLastUrl) return <SkipToNext onMount={advance} />;
    return (
      <StreamBlockedOverlay
        url={url}
        message="This source uses an insecure HTTP stream that the browser cannot embed on HTTPS."
        buttonText="Open Source"
      />
    );
  }

  // ── Website URL (col E) ──────────────────────────────────────────────────────
  if (!isYouTube && !isM3U8) {
    if (!isLastUrl) {
      return <SkipToNext onMount={advance} />;
    }

    // The embeddability check runs in the effect above; render a loader while it resolves.
    if (isWebsiteEmbeddable === null) {
      return <StreamLoader />;
    }

    if (!isWebsiteEmbeddable) {
      return <StreamBlockedOverlay url={url} message="The official website restricts embedding." buttonText="Visit Official Website" onAutoAdvance={!isLastUrl ? advance : null} />;
    }

    // Website is embeddable
    return (
      <>
        {isLoading && <StreamLoader />}
        <iframe
          key={`${channelKey}-website`}
          src={url}
          title="Live channel website"
          allow="autoplay; encrypted-media; fullscreen"
          allowFullScreen
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: "none" }}
          onLoad={onLoaded}
          onError={() => setIsLoading(false)}
        />
      </>
    );
  }

  // ── YouTube ──────────────────────────────────────────────────────────────────
  if (isYouTube) {
    // Case 1: direct video ID → SmartYouTubeEmbed (most reliable)
    let videoId = null;
    if (url.includes("watch?v=")) {
      videoId = url.split("v=")[1]?.split("&")[0] || null;
    } else if (url.includes("youtu.be/")) {
      videoId = url.split("youtu.be/")[1]?.split("?")[0] || null;
    } else if (url.includes("/embed/")) {
      videoId = url.split("/embed/")[1]?.split("?")[0] || null;
    }

    if (videoId) {
      return (
        <>
          {isLoading && <StreamLoader />}
          <div style={{ position: "absolute", inset: 0 }}>
            <SmartYouTubeEmbed
              videoId={videoId}
              isActive={true}
              onPlaying={onLoaded}
            />
          </div>
        </>
      );
    }

    // Case 2: channel /live URL e.g. youtube.com/@Channel/live
    // YouTube strictly blocks these via X-Frame-Options SAMEORIGIN.
    // Instead of rendering a broken iframe (sad face), we show a nice overlay.
    return (
      <StreamBlockedOverlay 
        url={url} 
        message="YouTube channel live streams cannot be embedded directly." 
        buttonText="Watch on YouTube" 
        onAutoAdvance={!isLastUrl ? advance : null}
      />
    );
  }

  // ── m3u8 stream ──────────────────────────────────────────────────────────────
  if (isM3U8) {
    if (streamTimedOut) {
      return (
        <StreamBlockedOverlay
          url={url}
          message="This live stream did not respond in time."
          buttonText="Open Stream"
          onAutoAdvance={!isLastUrl ? advance : null}
        />
      );
    }
    return (
      <>
        {isLoading && <StreamLoader />}
        <HlsVideoPlayer
          key={`${channelKey}-${urlIdx}`}
          src={url}
          className="live-player-video"
          onCanPlay={onLoaded}
          onFatalError={() => {
            if (!isLastUrl) {
              advance();
            } else {
              setStreamTimedOut(true);
              setIsLoading(false);
            }
          }}
        />
      </>
    );
  }

  return <div className="live-player-error">No playable stream found.</div>;
};

/** Calls onMount once after mount — used to skip non-embeddable website URLs when more remain */
const SkipToNext = ({ onMount }) => {
  useEffect(() => { onMount(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
};

export const liveLanguages = [
  "Afrikaans",
  "Albanian",
  "Amharic",
  "Arabic",
  "Armenian",
  "Assamese",
  "Aymara",
  "Azerbaijani",
  "Baruga",
  "Barwe",
  "Basque",
  "Belarusian",
  "Bengali",
  "Bhojpuri",
  "Bosnian",
  "Bulgarian",
  "Burmese",
  "Cantonese Chinese",
  "Catalan",
  "Kurdish",
  "Chamorro",
  "Chewa",
  "Chinese",
  "Croatian",
  "Czech",
  "Danish",
  "Dari",
  "Dhivehi",
  "Dzongkha",
  "Dutch",
  "English",
  "Estonian",
  "Filipino",
  "Finnish",
  "French",
  "Galician",
  "Georgian",
  "German",
  "Greek",
  "Guarani",
  "Gujarati",
  "Hai?om",
  "Haitian",
  "Hebrew",
  "Herero",
  "Hindi",
  "Hungarian",
  "Icelandic",
  "Imperial Aramaic (700-300 BCE)",
  "Indonesian",
  "Irish",
  "Italian",
  "Jamaican Creole English",
  "Japanese",
  "Kalanga",
  "Kannada",
  "Kazakh",
  "Khmer",
  "Kinyarwanda",
  "Kyrgyz",
  "Kongo",
  "Korean",
  "Kwangali",
  "Lao",
  "Latin",
  "Latvian",
  "Lingala",
  "Lithuanian",
  "Lozi",
  "Luba-Lulua",
  "Macedonian",
  "Malagasy",
  "Malay",
  "Malayalam",
  "Maltese",
  "Mandarin Chinese",
  "Maori",
  "Marathi",
  "Mongolian",
  "Montenegrin",
  "Multilingual",
  "Ndau",
  "Ndonga",
  "Nepali",
  "New Zealand Sign Language",
  "North Ndebele",
  "Northern Sotho",
  "Norwegian",
  "Odia",
  "Papiamento",
  "Pashto",
  "Persian",
  "Philippines",
  "Polish",
  "Portuguese",
  "Punjabi",
  "Quechua",
  "Romanian",
  "Russian",
  "Samoan",
  "Serbian",
  "Serbo-Croatian",
  "Shona",
  "Sinhala",
  "Slovak",
  "Slovenian",
  "Somali",
  "South Ndebele",
  "Southern Sotho",
  "Spanish",
  "Standard Moroccan Tamazight",
  "Swahili",
  "Swati",
  "Swedish",
  "Tajik",
  "Tamil",
  "Telugu",
  "Tigrinya",
  "Thai",
  "Tonga (Zambia)",
  "Tsonga",
  "Tsoa",
  "Tswana",
  "Turkish",
  "Turkmen",
  "Ukrainian",
  "Upper Guinea Crioulo",
  "Urdu",
  "Uzbek",
  "Venda",
  "Vietnamese",
  "Wolof",
  "Xhosa",
  "Yemen",
  "Zimbabwe Sign Language",
  "Zulu",
];

const LIVE_LANGUAGE_ALIASES = {
  bangla: "bengali",
  "central kurdish": "kurdish",
  "dari (parsi)": "dari",
  kirghiz: "kyrgyz",
};

const normalizeLiveLanguage = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  return LIVE_LANGUAGE_ALIASES[normalized] || normalized;
};

const liveChannelMatchesLanguage = (channelLanguage, selectedLanguage) => {
  const selected = normalizeLiveLanguage(selectedLanguage);
  if (!selected || selected === "all") return true;
  return String(channelLanguage || "")
    .split(/[;/,]/)
    .map(normalizeLiveLanguage)
    .includes(selected);
};

export const countries = [
  { code: "Global", name: "Global" },
  { code: "US", name: "United States" },
  ...[
    { code: "GB", name: "United Kingdom" },
    { code: "PK", name: "Pakistan" },
    { code: "IN", name: "India" },
    { code: "CA", name: "Canada" },
    { code: "AU", name: "Australia" },
    { code: "AE", name: "United Arab Emirates" },
    { code: "SA", name: "Saudi Arabia" },
    { code: "CN", name: "China" },
    { code: "RU", name: "Russia" },
    { code: "JP", name: "Japan" },
    { code: "DE", name: "Germany" },
    { code: "FR", name: "France" },
    { code: "IT", name: "Italy" },
    { code: "BR", name: "Brazil" },
    { code: "KR", name: "South Korea" },
    { code: "ES", name: "Spain" },
    { code: "MX", name: "Mexico" },
    { code: "ID", name: "Indonesia" },
    { code: "TR", name: "Turkey" },
    { code: "NL", name: "Netherlands" },
    { code: "PL", name: "Poland" },
    { code: "SE", name: "Sweden" },
    { code: "BE", name: "Belgium" },
    { code: "AR", name: "Argentina" },
    { code: "TH", name: "Thailand" },
    { code: "IL", name: "Israel" },
    { code: "IE", name: "Ireland" },
    { code: "NO", name: "Norway" },
    { code: "NG", name: "Nigeria" },
    { code: "EG", name: "Egypt" },
    { code: "ZA", name: "South Africa" },
    { code: "MY", name: "Malaysia" },
    { code: "VN", name: "Vietnam" },
    { code: "PH", name: "Philippines" },
    { code: "DK", name: "Denmark" },
    { code: "BD", name: "Bangladesh" },
    { code: "SG", name: "Singapore" },
    { code: "FI", name: "Finland" },
    { code: "NZ", name: "New Zealand" },
    { code: "GR", name: "Greece" },
    { code: "CL", name: "Chile" },
    { code: "CO", name: "Colombia" },
    { code: "IR", name: "Iran" },
    { code: "IQ", name: "Iraq" },
    { code: "KE", name: "Kenya" },
    { code: "LB", name: "Lebanon" },
    { code: "TW", name: "Taiwan" },
    { code: "UA", name: "Ukraine" },
    { code: "MA", name: "Morocco" },
    { code: "PS", name: "Palestine" },
    { code: "QA", name: "Qatar" },
    { code: "EU", name: "European Union" },
    { code: "SY", name: "Syria" },
    { code: "YE", name: "Yemen" },
    { code: "JO", name: "Jordan" },
    { code: "KW", name: "Kuwait" },
    { code: "OM", name: "Oman" },
    { code: "BH", name: "Bahrain" },
    { code: "TN", name: "Tunisia" },
    { code: "DZ", name: "Algeria" },
    { code: "LY", name: "Libya" },
    { code: "ET", name: "Ethiopia" },
    { code: "GH", name: "Ghana" },
    { code: "TZ", name: "Tanzania" },
    { code: "SD", name: "Sudan" },
    { code: "AF", name: "Afghanistan" },
    { code: "MM", name: "Myanmar" },
    { code: "KZ", name: "Kazakhstan" },
    { code: "AZ", name: "Azerbaijan" },
    { code: "GE", name: "Georgia" },
    { code: "AM", name: "Armenia" },
    { code: "BY", name: "Belarus" },
    { code: "RS", name: "Serbia" },
    { code: "HR", name: "Croatia" },
    { code: "RO", name: "Romania" },
    { code: "CZ", name: "Czech Republic" },
    { code: "HU", name: "Hungary" },
    { code: "SK", name: "Slovakia" },
    { code: "PT", name: "Portugal" },
    { code: "AT", name: "Austria" },
    { code: "CH", name: "Switzerland" },
  ].sort((a, b) => a.name.localeCompare(b.name)),
];

// ✅ NEW: favicon helper (simple “logo” without backend changes)
const getFaviconLogo = (url) => {
  try {
    const host = new URL(url).hostname;
    // Google favicon service (small icon, but works for most websites)
    return `https://www.google.com/s2/favicons?domain=${host}&sz=64`;
  } catch {
    return null;
  }
};

const ScrollChevron = ({ direction }) => {
  // Geometry is intentionally exact rather than font/glyph based. With endpoints
  // at y=8/92 and a 17.397-unit horizontal run to the vertex, the included
  // angle is 135°. Butt caps + miter joins keep the outer ends and center sharp.
  const points = direction === "left"
    ? "28.397,8 11,50 28.397,92"
    : "11,8 28.397,50 11,92";
  return (
    <svg
      className="scroll-arrow__shape"
      viewBox="0 0 40 100"
      aria-hidden="true"
      focusable="false"
    >
      <polyline
        points={points}
        fill="none"
        stroke="currentColor"
        strokeWidth="14.5"
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit="10"
      />
    </svg>
  );
};

const CustomPrevArrow = (props) => {
  const { className, style, onClick } = props;
  const disabled = className.includes("slick-disabled");
  return (
    <button
      type="button"
      className={`${className} scroll-arrow left`}
      style={{ ...style, display: "flex" }}
      onClick={onClick}
      disabled={disabled}
      aria-disabled={disabled}
      aria-label="Scroll left"
    >
      <ScrollChevron direction="left" />
    </button>
  );
};

const CustomNextArrow = (props) => {
  const { className, style, onClick } = props;
  if (className.includes("slick-disabled")) return null;
  return (
    <button
      type="button"
      className={`${className} scroll-arrow right`}
      style={{ ...style, display: "flex" }}
      onClick={onClick}
      aria-label="Scroll right"
    >
      <ScrollChevron direction="right" />
    </button>
  );
};

const sliderBaseSettings = {
  dots: false,
  infinite: false,
  speed: 500,
  slidesToShow: 5,
  slidesToScroll: 1,
  prevArrow: <CustomPrevArrow />,
  nextArrow: <CustomNextArrow />,
  responsive: [
    { breakpoint: 1880, settings: { slidesToShow: 4 } },
    { breakpoint: 1514, settings: { slidesToShow: 3 } },
    { breakpoint: 1152, settings: { slidesToShow: 2 } },
    { breakpoint: 768, settings: { slidesToShow: 3  } },
  ],
};



const ActiveVideoStateContext = React.createContext({ activeVideoKey: null, mobileHeroVideoId: null, mobileHeroTag: null });
const ActiveVideoDispatchContext = React.createContext({ setActiveVideoKey: () => {}, setMobileHeroVideoId: () => {}, setMobileHeroTag: () => {} });

const ConnectedYouTubeCard = React.memo(({ video, vIdx, sliderRef, tag }) => {
  const { activeVideoKey } = React.useContext(ActiveVideoStateContext);
  const { setActiveVideoKey, setMobileHeroVideoId, setMobileHeroTag } = React.useContext(ActiveVideoDispatchContext);

  const uniqueKey = tag?.raw_title ? `${tag.raw_title}-${video.video_id}` : video.video_id;

  const handleVideoEnd = React.useCallback(() => {
    if (vIdx < tag.videos.length - 1) {
      const nextVideo = tag.videos[vIdx + 1];
      const nextUniqueKey = tag?.raw_title ? `${tag.raw_title}-${nextVideo.video_id}` : nextVideo.video_id;
      setActiveVideoKey(nextUniqueKey);
      if (sliderRef.current) {
        sliderRef.current.slickGoTo(vIdx + 1);
      }
    } else {
      setActiveVideoKey(null);
    }
  }, [vIdx, tag, setActiveVideoKey, sliderRef]);

  return (
    <YouTubeCard
      videoId={video.video_id}
      channelCountry={video.channel_country}
      channelName={video.channel_title}
      publishedAt={video.published_at}
      isPlaying={activeVideoKey === uniqueKey}
      onPlay={() => {
        setActiveVideoKey(uniqueKey);
        setMobileHeroVideoId(null);
        setMobileHeroTag(null);
      }}
      onEnded={handleVideoEnd}
      onMobileClick={(vid) => {
        setMobileHeroVideoId(vid);
        setMobileHeroTag(tag);
        setActiveVideoKey(null);
        
        // Smooth scroll to top on mobile so the player is in focus
        window.scrollTo({ top: 0, behavior: "smooth" });
        const trendingScroll = document.querySelector(".trending-scroll");
        if (trendingScroll) {
          trendingScroll.scrollTo({ top: 0, behavior: "smooth" });
        }
      }}
    />
  );
});

/** Clickable headline that opens the AI media analysis modal */
const AdaptiveHeadlineClickable = ({ tag, tagIndex }) => {
  const { openAnalysis } = React.useContext(AppContext);
  const handleOpenAnalysis = () => {
    // Prefer pre-computed 29-metric analysis_json; fall back to legacy ai_analysis text
    const cached = tag.analysis_json
      ? (typeof tag.analysis_json === "string" ? tag.analysis_json : JSON.stringify(tag.analysis_json))
      : (tag.ai_analysis || "");
    openAnalysis(tag.title, "", tag.raw_title, cached, tag.id, tag.ai_video_url || "", tag.analysis_generated_at || null, tag.videos || [], tag.event_id || null);
  };

  return (
    <>
      <EngagementBar tag={tag} className="headline-and-engagement__comment" />
      <AdaptiveHeadline
        as="h4"
        className="text-light news-block__title news-block__title--clickable"
        data-vip-second-headline={tagIndex === 1 ? "true" : undefined}
        desktopMaxLines={1}
        mobileMaxLines={2}
        onClick={handleOpenAnalysis}
        title="Click for AI media analysis"
        style={{ cursor: "pointer" }}
      >
        <u>{tag.title}</u>
      </AdaptiveHeadline>
    </>
  );
};

const HeroAiHint = React.memo(() => {
  const [visibleText, setVisibleText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const totalChars = HOMEPAGE_AI_HINT_FULL_TEXT.length;
    const typingStepMs = Math.max(20, Math.round(HOMEPAGE_AI_HINT_TYPE_MS / totalChars));
    const deletingStepMs = Math.max(16, Math.round(HOMEPAGE_AI_HINT_DELETE_MS / totalChars));

    const timerId = window.setTimeout(() => {
      if (!isDeleting) {
        if (visibleText.length < HOMEPAGE_AI_HINT_FULL_TEXT.length) {
          setVisibleText(HOMEPAGE_AI_HINT_FULL_TEXT.slice(0, visibleText.length + 1));
          return;
        }

        setIsDeleting(true);
        return;
      }

      if (visibleText.length > 0) {
        setVisibleText(HOMEPAGE_AI_HINT_FULL_TEXT.slice(0, visibleText.length - 1));
      }
    }, !isDeleting
      ? (visibleText.length < HOMEPAGE_AI_HINT_FULL_TEXT.length
        ? typingStepMs
        : HOMEPAGE_AI_HINT_HOLD_MS)
      : deletingStepMs);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [isDeleting, visibleText]);

  const accentText = visibleText.slice(0, HOMEPAGE_AI_HINT_ACCENT_TEXT.length);
  const restText = visibleText.slice(HOMEPAGE_AI_HINT_ACCENT_TEXT.length);

  return (
    <div
      className="vip-hero-ai-hint"
      aria-hidden="true"
      style={{ "--homepage-ai-hint-total-ms": `${HOMEPAGE_AI_HINT_TOTAL_MS}ms` }}
    >
      <span className="vip-hero-ai-hint__pointer">
        <span
          className="ai-tap-icon"
          style={{ "--ai-tap-icon-mask": `url(${doubleTapIcon})` }}
          aria-hidden="true"
        />
      </span>
      <span
        className="vip-hero-ai-hint__label"
        data-empty={visibleText.length === 0 ? "true" : "false"}
      >
        <span className="vip-hero-ai-hint__label-text">
          <span className="vip-hero-ai-hint__label-accent">{accentText}</span>
          <span className="vip-hero-ai-hint__label-rest">{restText}</span>
        </span>
      </span>
    </div>
  );
});

const EngagementBar = React.memo(({ tag, className = "" }) => {
  const { engagement, openComments } = React.useContext(AppContext);
  if (!tag || !tag.id) return null;
  
  const eng = engagement[String(tag.id)] || {};
  const commentsCount = eng.comments || 0;

  return (
    <button
      type="button"
      className={`blinking-comment-icon ${className}`.trim()}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        openComments(tag.id, tag.title);
      }}
      title="Comments"
      aria-label={commentsCount > 0 ? `Comments (${commentsCount})` : "Comments"}
    >
      <svg className="comment-bubble-svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M4.5 4.5h15v11h-9.1L6 19.2v-3.7H4.5z" />
      </svg>
      {commentsCount > 0 && <span className="count">{commentsCount}</span>}
    </button>
  );
});

const NewsRow = React.memo(({ tag, tagIndex }) => {
  const sliderRef = useRef(null);

  // RSS article card — no YouTube video available
  if (tag.videos.length === 0 && tag.article_url) {
    return (
      <div
        className={`slider-container position-relative slider-row ${tagIndex === 0 ? 'first-row' : ''}`}
        data-vip-first-row={tagIndex === 0 ? "true" : undefined}
      >
        {tagIndex > 0 && (
          <div className="headline-and-engagement">
            <AdaptiveHeadlineClickable tag={tag} tagIndex={tagIndex} />
          </div>
        )}
        <a
          href={tag.article_url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: "none" }}
        >
          <div
            style={{
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: "10px",
              padding: "16px 20px",
              margin: "6px 0",
              display: "flex",
              alignItems: "flex-start",
              gap: "14px",
              cursor: "pointer",
              transition: "background 0.2s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.10)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.05)")}
          >
            <div style={{ flex: 1 }}>
              <span
                style={{
                  display: "inline-block",
                  background: "rgba(99,102,241,0.25)",
                  color: "#ffffff",
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  padding: "2px 8px",
                  borderRadius: "999px",
                  marginBottom: "8px",
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                }}
              >
                {tag.categoryL1} {tag.origin_country ? `· ${tag.origin_country}` : ""}
              </span>
              <h4
                style={{
                  color: "#f1f5f9",
                  margin: 0,
                  fontSize: "0.95rem",
                  lineHeight: 1.45,
                  fontWeight: 500,
                }}
              >
                {tag.title}
              </h4>
            </div>
            <span style={{ color: "#6366f1", fontSize: "1.2rem", marginTop: "4px" }}>↗</span>
          </div>
        </a>
      </div>
    );
  }

  // YouTube video row
  return (
    <div 
      className={`slider-container position-relative slider-row ${tagIndex === 0 ? 'first-row' : ''}`}
      data-vip-first-row={tagIndex === 0 ? "true" : undefined}
    >
      {tagIndex > 0 && (
        <div className="headline-and-engagement">
          <AdaptiveHeadlineClickable tag={tag} tagIndex={tagIndex} />
        </div>
      )}

      <Slider 
        className="vip-row-slider"
        ref={sliderRef} 
        {...sliderBaseSettings}
      >
        {tag.videos.map((video, vIdx) => (
          <div key={video.video_id} className="px-1 px-md-2">
            <ConnectedYouTubeCard video={video} vIdx={vIdx} sliderRef={sliderRef} tag={tag} />
          </div>
        ))}
      </Slider>
    </div>
  );
});

function App() {
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const [selectedCountry, setSelectedCountry] = useState("Global");
  const [selectedLanguage, setSelectedLanguage] = useState("English");
  const [videos, setVideos] = useState([]);
  const [playingLiveChannel, setPlayingLiveChannel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("trending");
  const [location, setLocation] = useState({ lat: null, lng: null });
  const [zoom, setZoom] = useState(2);
  const [routeInfo, setRouteInfo] = useState(null);
  const [trendingTags, setTrendingTags] = useState([]);
  const [activeVideoKey, setActiveVideoKey] = useState(null);
  const [playingRowId, setPlayingRowId] = useState(null);
  const [offset, setOffset] = useState(0);
  const [loadingMore, setLoadingMore] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  // Trending-tab streaming state
  const [trendingOffset, setTrendingOffset] = useState(0);
  const [hasMoreTrending, setHasMoreTrending] = useState(true);
  const [loadingMoreTrending, setLoadingMoreTrending] = useState(false);
  const [vipHeroImageLoaded, setVipHeroImageLoaded] = useState(false);
  const TRENDING_PAGE_SIZE = 50;
  const [activeCategory, setActiveCategory] = useState({ l1: "", l2: "" });
  const [selectedEventCountry, setSelectedEventCountry] = useState("");
  const [eventCountryOptions, setEventCountryOptions] = useState([]);

  // Live TV state
  const [liveChannels, setLiveChannels] = useState([]);
  const [liveChannelsLoading, setLiveChannelsLoading] = useState(false);
  const [liveChannelsError, setLiveChannelsError] = useState(null);
  const [liveSearch, setLiveSearch] = useState("");
  const [liveFilterIptv, setLiveFilterIptv] = useState(false);
  const [liveFilterYt, setLiveFilterYt] = useState(false);
  const isMobileScreen = useIsMobileScreen(768);
  const [mobileHeroVideoId, setMobileHeroVideoId] = useState(null);
  const [mobileHeroTag, setMobileHeroTag] = useState(null);

  const activeVideoState = React.useMemo(() => ({ activeVideoKey, mobileHeroVideoId, mobileHeroTag }), [activeVideoKey, mobileHeroVideoId, mobileHeroTag]);
  const activeVideoDispatch = React.useMemo(() => ({ setActiveVideoKey, setMobileHeroVideoId, setMobileHeroTag }), [setActiveVideoKey, setMobileHeroVideoId, setMobileHeroTag]);

  const homeMainRef = useRef(null);
  const footerRef = useRef(null);
  const footerVisibleRef = useRef(true);
  const [footerScrollHintVisible, setFooterScrollHintVisible] = useState(true);
  const [footerHidden, setFooterHidden] = useState(false);
  const [headerSubfooterVisible, setHeaderSubfooterVisible] = useState(false);
  const vipAfterHeroRef = useRef(null);
  const mapRef = useRef(null);
  const routingControlRef = useRef(null);
  const rowRefs = useRef([]);
  const pendingScrollToTopRef = useRef(false);
  // Request generations prevent slower/stale responses from an old filter
  // selection from overwriting the newest UI state. This matters most on
  // mobile or slower networks where users can change selectors quickly.
  const trendingRequestVersionRef = useRef(0);
  const trendingRetryTimerRef = useRef(null);
  const videosRequestVersionRef = useRef(0);
  const liveRequestVersionRef = useRef(0);
  const liveChannelsQueryKeyRef = useRef("");
  const [canScrollLeft, setCanScrollLeft] = useState([]);
  const [canScrollRight, setCanScrollRight] = useState([]);

  const [hasMoved, setHasMoved] = useState(false);
  const vipLastStableOverlapRef = useRef(320);
  const vipHasInlinePlaybackRef = useRef(false);
  const vipScheduleMeasureRef = useRef(() => {});

  const hasInlinePlayback = Boolean(activeVideoKey) || Boolean(mobileHeroVideoId);

  useEffect(() => () => {
    trendingRequestVersionRef.current += 1;
    videosRequestVersionRef.current += 1;
    liveRequestVersionRef.current += 1;
    liveChannelsQueryKeyRef.current = "";
    if (trendingRetryTimerRef.current) {
      window.clearTimeout(trendingRetryTimerRef.current);
      trendingRetryTimerRef.current = null;
    }
  }, []);

  useLayoutEffect(() => {
    const main = homeMainRef.current;
    const footer = footerRef.current;
    if (!main || !footer) return;

    let frameId = 0;
    let resizeObserver;

    const updateFooterHeight = () => {
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(() => {
        const rect = footer.getBoundingClientRect();
        const height = Math.max(0, Math.round(rect.height || 0));
        if (height) main.style.setProperty("--footer-height", `${height}px`);
        if (footerVisibleRef.current) {
          vipScheduleMeasureRef.current();
        }
      });
    };

    updateFooterHeight();
    window.addEventListener("resize", updateFooterHeight);

    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(updateFooterHeight);
      resizeObserver.observe(footer);
    }

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", updateFooterHeight);
      resizeObserver?.disconnect();
    };
  }, []);

  useLayoutEffect(() => {
    const BOTTOM_SLACK = 8;
    const SCROLL_TOP_THRESHOLD = 4;
    const FOOTER_SHOW_TRANSITION_MS = 300;
    const isMobileVipAfterHeroScroll =
      isMobileScreen && activeTab === "trending";
    const footerTopThreshold = isMobileVipAfterHeroScroll
      ? 0
      : SCROLL_TOP_THRESHOLD;
    const getMobileScrollContainer = () => vipAfterHeroRef.current;
    const showTransitionTimeoutIds = new Set();

    const getScrollMetrics = () => {
      if (isMobileVipAfterHeroScroll) {
        const scrollEl = getMobileScrollContainer();
        if (scrollEl) {
          return {
            scrollTop: scrollEl.scrollTop,
            viewH: scrollEl.clientHeight,
            fullH: scrollEl.scrollHeight,
          };
        }
      }

      const docEl = document.documentElement;
      const bodyEl = document.body;
      return {
        scrollTop:
          window.scrollY ??
          docEl.scrollTop ??
          bodyEl.scrollTop ??
          0,
        viewH: window.innerHeight,
        fullH: Math.max(
          docEl.scrollHeight,
          bodyEl.scrollHeight,
          docEl.offsetHeight,
          bodyEl.offsetHeight,
        ),
      };
    };

    const syncFooterVisibility = (scrollTop) => {
      const wasVisible = footerVisibleRef.current;
      const footerVisible = scrollTop <= footerTopThreshold;
      footerVisibleRef.current = footerVisible;
      if (footerVisible && !wasVisible) {
        vipScheduleMeasureRef.current();
        const timeoutId = window.setTimeout(() => {
          showTransitionTimeoutIds.delete(timeoutId);
          vipScheduleMeasureRef.current();
        }, FOOTER_SHOW_TRANSITION_MS);
        showTransitionTimeoutIds.add(timeoutId);
      }
      return footerVisible;
    };

    const updateFooterScrollHint = () => {
      const { scrollTop, viewH, fullH } = getScrollMetrics();
      const footerVisible = syncFooterVisibility(scrollTop);
      const canScrollDown = fullH > viewH + 2;
      const atBottom = scrollTop + viewH >= fullH - BOTTOM_SLACK;
      setFooterHidden(!footerVisible);
      setFooterScrollHintVisible(canScrollDown && !atBottom);
      setHeaderSubfooterVisible(scrollTop > footerTopThreshold);
    };

    let frameId = 0;
    const mobileScrollListenerOptions = { passive: true, capture: true };
    const scheduleUpdate = () => {
      syncFooterVisibility(getScrollMetrics().scrollTop);
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(updateFooterScrollHint);
    };

    const mobileScrollContainer = getMobileScrollContainer();
    scheduleUpdate();
    window.addEventListener("resize", scheduleUpdate);
    if (isMobileVipAfterHeroScroll) {
      window.addEventListener("scroll", scheduleUpdate, mobileScrollListenerOptions);
      if (mobileScrollContainer) {
        mobileScrollContainer.addEventListener("scroll", scheduleUpdate, {
          passive: true,
        });
      }
    } else {
      window.addEventListener("scroll", scheduleUpdate, { passive: true });
    }

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(scheduleUpdate);
      if (isMobileVipAfterHeroScroll && mobileScrollContainer) {
        resizeObserver.observe(mobileScrollContainer);
        const mobileScrollContent = mobileScrollContainer.firstElementChild;
        if (mobileScrollContent) resizeObserver.observe(mobileScrollContent);
      } else {
        resizeObserver.observe(document.documentElement);
        if (document.body) resizeObserver.observe(document.body);
      }
    }

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", scheduleUpdate);
      if (isMobileVipAfterHeroScroll) {
        window.removeEventListener("scroll", scheduleUpdate, mobileScrollListenerOptions);
        if (mobileScrollContainer) {
          mobileScrollContainer.removeEventListener("scroll", scheduleUpdate);
        }
      } else {
        window.removeEventListener("scroll", scheduleUpdate);
      }
      showTransitionTimeoutIds.forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
      showTransitionTimeoutIds.clear();
      resizeObserver?.disconnect();
    };
  }, [routeLocation.pathname, activeTab, isMobileScreen]);

  useLayoutEffect(() => {
    // IMPORTANT: cancel any pending measure frame FIRST, before updating the ref.
    // This prevents the double-scheduled rAF from the measure effect running
    // in the brief window between the click event and the ref being set true.
    if (hasInlinePlayback) {
      // Flush any pending scheduled measure — we don't want it to run now.
      vipScheduleMeasureRef.current = () => {};
    }
    vipHasInlinePlaybackRef.current = hasInlinePlayback;
    const main = homeMainRef.current;
    if (!main) return;
    if (hasInlinePlayback) {
      // Freeze the overlap at its current measured value when a video starts
      // playing — do NOT zero it, as that causes the rows to jump downward.
      // We explicitly re-apply the last stable value to ensure it survives
      // any potential React re-renders of the main container.
      main.style.setProperty("--vip-after-hero-overlap", `${vipLastStableOverlapRef.current}px`);
    } else {
      // Playback stopped — re-measure so the rows slide back up naturally.
      vipScheduleMeasureRef.current();
    }
  }, [hasInlinePlayback]);

  const filteredTrendingTags = useMemo(
    () =>
      trendingTags
        .filter((tag) => {
          if (!tag.videos || tag.videos.length <= 2) return false;
          // Server-side filtering is authoritative, but keep a normalized client
          // check so the previous selection disappears immediately while the next
          // request is in flight. Avoid strict casing/whitespace false negatives.
          const normalizeLabel = (value) => String(value || "").trim().toLocaleLowerCase();
          if (activeCategory.l1 && normalizeLabel(tag.categoryL1) !== normalizeLabel(activeCategory.l1)) return false;
          if (activeCategory.l2 && normalizeLabel(tag.categoryL2) !== normalizeLabel(activeCategory.l2)) return false;
          if (selectedEventCountry) {
            const selectedCode = String(selectedEventCountry).trim().toUpperCase();
            const eventCodes = Array.isArray(tag.countries)
              ? tag.countries.map((code) => String(code || "").trim().toUpperCase())
              : [];
            if (!eventCodes.includes(selectedCode)) return false;
          }
          return true;
        })
        .map((tag) => {
          const seenIds = new Set();
          const uniqueVideos = (tag.videos || []).filter((v) => {
            if (seenIds.has(v.video_id)) return false;
            seenIds.add(v.video_id);
            return true;
          });
          return { ...tag, videos: uniqueVideos };
        }),
    [trendingTags, activeCategory, selectedEventCountry],
  );

  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    const main = homeMainRef.current;
    const footer = footerRef.current;
    const rowsRoot = vipAfterHeroRef.current;

    if (!main || !footer || !rowsRoot) return;

    if (activeTab !== "trending" || window.innerWidth <= HEADLINE_MOBILE_BREAKPOINT) {
      vipLastStableOverlapRef.current = 0;
      main.style.removeProperty("--vip-after-hero-overlap");
      vipScheduleMeasureRef.current = () => {};
      return;
    }

    let frameId = 0;
    const visualViewport = window.visualViewport;
    const isNearTop = () => (window.scrollY ?? window.pageYOffset ?? 0) <= 4;
    const isFooterVisible = () => footerVisibleRef.current;
    let resizeObserver;

    const applyOverlap = (value) => {
      const nextOverlap = Math.max(0, Math.min(460, Math.round(value)));
      if (nextOverlap === vipLastStableOverlapRef.current) return;
      vipLastStableOverlapRef.current = nextOverlap;
      main.style.setProperty("--vip-after-hero-overlap", `${nextOverlap}px`);
    };

    const measureOverlap = () => {
      if (vipHasInlinePlaybackRef.current || !isNearTop() || !isFooterVisible()) return;
      const secondHeadline = rowsRoot.querySelector("[data-vip-second-headline='true']");
      if (!secondHeadline) {
        return applyOverlap(vipLastStableOverlapRef.current || 320);
      }

      const currentOverlap = vipLastStableOverlapRef.current || 320;
      const footerRect = footer.getBoundingClientRect();
      const headlineRect = secondHeadline.getBoundingClientRect();
      const rawBottomOffset = parseFloat(window.getComputedStyle(main).getPropertyValue("--vip-overlap-bottom-offset"));
      const bottomOffset = Number.isFinite(rawBottomOffset) ? rawBottomOffset : 2;
      const rawFooterHeight = parseFloat(
        window.getComputedStyle(main).getPropertyValue("--footer-height"),
      );
      const footerHeight = Math.max(
        0,
        Math.round(footerRect.height || rawFooterHeight || 0),
      );
      const headlineHeight = Math.max(0, Math.round(headlineRect.height || 0));
      // Footer is position:fixed at bottom:0 — use the settled viewport position
      // instead of getBoundingClientRect().top, which is wrong during show/hide transitions.
      const footerTop = window.innerHeight - footerHeight;
      // Align the headline by its top edge so the whole line sits behind the footer.
      // The previous bottom-edge math ignored headline height, which could leave
      // a strip of text sticking out above the footer on initial load.
      const targetTop = footerTop + bottomOffset;
      const targetBottom = targetTop + headlineHeight;
      const naturalBottom = headlineRect.bottom + currentOverlap;
      applyOverlap(naturalBottom - targetBottom);
    };

    const scheduleMeasure = () => {
      if (vipHasInlinePlaybackRef.current || !isNearTop() || !isFooterVisible()) return;
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(() => {
        frameId = window.requestAnimationFrame(measureOverlap);
      });
    };

    const handleViewportResize = () => {
      if (!isNearTop() || !isFooterVisible()) return;
      scheduleMeasure();
    };

    vipScheduleMeasureRef.current = scheduleMeasure;
    main.style.setProperty("--vip-after-hero-overlap", `${vipLastStableOverlapRef.current}px`);

    if (isNearTop() && isFooterVisible()) {
      scheduleMeasure();
      document.fonts?.ready?.then(() => {
        if (isNearTop() && isFooterVisible()) scheduleMeasure();
      }).catch(() => {});
    }

    window.addEventListener("resize", handleViewportResize);
    window.addEventListener("scroll", handleViewportResize, { passive: true });
    visualViewport?.addEventListener("resize", handleViewportResize);

    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        scheduleMeasure();
      });
      resizeObserver.observe(rowsRoot);
      resizeObserver.observe(footer);
      const hero = main.querySelector(".vip-hero");
      const secondHeadline = rowsRoot.querySelector("[data-vip-second-headline='true']");
      if (hero) resizeObserver.observe(hero);
      if (secondHeadline) resizeObserver.observe(secondHeadline);
    }

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", handleViewportResize);
      window.removeEventListener("scroll", handleViewportResize);
      visualViewport?.removeEventListener("resize", handleViewportResize);
      resizeObserver?.disconnect();
    };
  }, [activeTab, filteredTrendingTags, loadingMore, vipHeroImageLoaded]);

  useLayoutEffect(() => {
    if (footerHidden) return;
    const FOOTER_SHOW_TRANSITION_MS = 300;
    vipScheduleMeasureRef.current();
    const timeoutId = window.setTimeout(
      () => vipScheduleMeasureRef.current(),
      FOOTER_SHOW_TRANSITION_MS,
    );
    return () => window.clearTimeout(timeoutId);
  }, [footerHidden]);

  useLayoutEffect(() => {
    if (typeof window === "undefined" || !pendingScrollToTopRef.current) return;

    pendingScrollToTopRef.current = false;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });

    const frameId = window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [activeTab, selectedCountry, selectedLanguage, selectedEventCountry, activeCategory]);

  useEffect(() => {
    if (activeTab === "trending") {
      setTrendingTags([]);
      setTrendingOffset(0);
      setHasMoreTrending(true);
      fetchGlobalNews(selectedCountry, selectedLanguage, false, 0);
    } else if (activeTab === "videos") {
      setVideos([]);
      setOffset(0);
      setHasMore(true);
      fetchVideos(selectedCountry, 0, null, null, selectedLanguage);
    }
  }, [selectedCountry, selectedLanguage, selectedEventCountry, activeCategory.l1, activeCategory.l2, activeTab]);

  useEffect(() => {
    if (loadingMore) {
      setVipHeroImageLoaded(false);
    }
  }, [loadingMore]);


  // Infinite scroll for the Videos tab (global-news is single-shot, no scroll pagination needed)
  useEffect(() => {
    if (activeTab !== "videos") return;

    const handleScroll = (e) => {
      if (loadingMore || !hasMore) return;
      
      let scrollTop, clientHeight, scrollHeight;
      if (e && e.target && e.target.nodeType === 1) {
        if (e.target.scrollWidth > e.target.clientWidth && e.target.scrollHeight === e.target.clientHeight) return;
        scrollTop = e.target.scrollTop;
        clientHeight = e.target.clientHeight;
        scrollHeight = e.target.scrollHeight;
      } else {
        scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
        clientHeight = window.innerHeight || document.documentElement.clientHeight;
        scrollHeight = document.documentElement.scrollHeight || document.body.scrollHeight || 0;
      }
      
      if (scrollTop + clientHeight >= scrollHeight - 200) {
        setOffset((prev) => {
          if (prev > offset) return prev;
          const nextOffset = prev + 12;
          setTimeout(() => fetchVideos(selectedCountry, nextOffset, null, null, selectedLanguage), 0);
          return nextOffset;
        });
      }
    };

    window.addEventListener("scroll", handleScroll, { capture: true, passive: true });
    return () => window.removeEventListener("scroll", handleScroll, { capture: true });
  }, [offset, loadingMore, hasMore, activeTab, selectedCountry, selectedLanguage]);

  // Infinite scroll for the Trending tab
  useEffect(() => {
    if (activeTab !== "trending") return;

    const handleTrendingScroll = (e) => {
      if (loadingMore || loadingMoreTrending || !hasMoreTrending) return;
      
      let scrollTop, clientHeight, scrollHeight;
      if (e && e.target && e.target.nodeType === 1) {
        if (e.target.scrollWidth > e.target.clientWidth && e.target.scrollHeight === e.target.clientHeight) return;
        scrollTop = e.target.scrollTop;
        clientHeight = e.target.clientHeight;
        scrollHeight = e.target.scrollHeight;
      } else {
        scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
        clientHeight = window.innerHeight || document.documentElement.clientHeight;
        scrollHeight = document.documentElement.scrollHeight || document.body.scrollHeight || 0;
      }
      
      if (scrollTop + clientHeight >= scrollHeight - 400) {
        setTrendingOffset((prev) => {
          if (prev > trendingOffset) return prev;
          const nextOffset = prev + TRENDING_PAGE_SIZE;
          setTimeout(() => fetchGlobalNews(selectedCountry, selectedLanguage, false, nextOffset), 0);
          return nextOffset;
        });
      }
    };

    window.addEventListener("scroll", handleTrendingScroll, { capture: true, passive: true });
    return () => window.removeEventListener("scroll", handleTrendingScroll, { capture: true });
  }, [loadingMore, loadingMoreTrending, hasMoreTrending, activeTab, selectedCountry, selectedEventCountry, selectedLanguage, activeCategory.l1, activeCategory.l2, trendingOffset]);

  /** Extract a YouTube video_id from any YouTube URL, or null. */
  const extractYtId = (url) => {
    if (!url) return null;
    if (url.includes("/live")) return null;
    try {
      const u = new URL(url);
      if (u.hostname.includes("youtu.be"))
        return u.pathname.slice(1).split("?")[0];
      return u.searchParams.get("v") || null;
    } catch {
      return null;
    }
  };

  const sortTags = (tags, region, lang) => {
    // If a specific region is selected, respect the chronological RSS order (newest fetched_at first)
    if (region && region !== "Global") {
      return tags.sort((a, b) => {
        return (b.fetched_at || "").localeCompare(a.fetched_at || "");
      });
    }

    // For Global view: always show US news first, then global.
    // Tier 3 — Latest US news: US origin + covers a US subject (newest first)
    // Tier 2 — Other US news:  US origin only (newest first)
    // Tier 1 — Global news:    everything else (newest first)
    const getTier = (tag) => {
      const isUsOutlet  = tag.origin_country === "US";
      const isUsSubject = Array.isArray(tag.countries) && tag.countries.includes("US");
      if (isUsOutlet && isUsSubject) return 3; // Latest US news
      if (isUsOutlet)                return 2; // Other US news
      return 1;                                // Global news
    };

    return tags.sort((a, b) => {
      const tierDiff = getTier(b) - getTier(a);
      if (tierDiff !== 0) return tierDiff; // Higher tier first
      // Within the same tier, sort newest first
      return (b.fetched_at || "").localeCompare(a.fetched_at || "");
    });
  };

  /** Convert a raw API rows array into deduplicated, sorted tag objects. */
  const processNewsRows = (rows, region, lang) => {
    const tags = rows.map((row) => {
      const allVids = Array.isArray(row.all_videos) ? row.all_videos : [];
      const videos = allVids
        .map((v) => ({
          video_id: extractYtId(v.video_url),
          channel_title: v.channel_title || v.channel || "",
          channel_country: v.channel_country || "",
          // Backend stores duration as `duration_sec` (int). Fall back to `duration` for
          // older rows that may still use that field name.
          duration_sec: v.duration_sec ?? (typeof v.duration === "number" ? v.duration : null),
          published_at: v.published_at || null,
          thumbnail:
            v.thumbnail ||
            `https://img.youtube.com/vi/${extractYtId(v.video_url)}/hqdefault.jpg`,
        }))
        .filter((v) => {
          if (!v.video_id) return false;
          // Only show videos where duration is known and within 30s–6min
          // Videos with null duration are excluded (no longer kept as historical data)
          if (v.duration_sec === null || v.duration_sec === undefined) return false;
          if (v.duration_sec < 30 || v.duration_sec > 360) return false;
          return true;
        })
        // Sort newest-first so the most recent video appears on the left
        .sort((a, b) => (b.published_at || "").localeCompare(a.published_at || ""))
        // Deduplicate by channel: keep only the newest video per channel_title
        .filter((() => {
          const seen = new Map();
          return (v) => {
            const key = (v.channel_title || "").toLowerCase().trim() || v.video_id;
            if (seen.has(key)) return false;
            seen.set(key, true);
            return true;
          };
        })());

      // No fallback to row.video_url without duration — we cannot verify its length.

      const article_url =
        videos.length === 0 && row.video_url ? row.video_url : null;

      return {
        id: row.id || null,  // news_item_id for engagement system
        event_id: row.event_id || null,
        title: row.title
          ? row.title.includes(" - ")
            ? row.title.substring(0, row.title.lastIndexOf(" - ")).trim()
            : row.title
          : "",
        raw_title: row.title || "",
        videos,
        article_url,
        source: row.source || "",
        origin_country: row.origin_country || "",
        countries: row.countries || [],
        categoryL1: row.categories?.level1 || "Uncategorized",
        categoryL2: row.categories?.level2 || "General",
        news_date: row.news_date,
        fetched_at: row.fetched_at,
        ai_analysis: row.ai_analysis || "",
        analysis_json: row.analysis_json || null,
        analysis_generated_at: row.analysis_generated_at || null,
      };
    // Keep analyzed events in the feed when coverage is genuinely empty. The
    // analysis modal has an explicit no-video state; raw/unanalysed rows stay
    // out of the clickable feed.
    }).filter(t =>
      (t.videos && t.videos.length > 0) ||
      (t.event_id && (t.analysis_json || t.ai_analysis))
    );

    return sortTags(tags, region, lang);
  };

  /**
   * Fetch headlines from /global-news in pages of TRENDING_PAGE_SIZE.
   * append=false resets the list (new country/language selection).
   */
  const fetchGlobalNews = async (region, lang, _retrying = false, pageOffset = 0, _requestVersion = null) => {
    const isAppend = pageOffset > 0;
    const requestVersion = _requestVersion ?? (
      (isAppend || _retrying)
        ? trendingRequestVersionRef.current
        : ++trendingRequestVersionRef.current
    );
    let retryScheduled = false;

    if (!isAppend && !_retrying && _requestVersion == null && trendingRetryTimerRef.current) {
      window.clearTimeout(trendingRetryTimerRef.current);
      trendingRetryTimerRef.current = null;
    }

    try {
      if (requestVersion !== trendingRequestVersionRef.current) return;
      if (isAppend) {
        setLoadingMoreTrending(true);
      } else {
        setLoadingMore(true);
        if (!_retrying) setTrendingTags([]);
      }

      const params = { limit: TRENDING_PAGE_SIZE, offset: pageOffset };
      if (region && region !== "Global") {
        params.origin_country = region;
        // Media-outlet country and event country are distinct filters.
      }
      if (selectedEventCountry) params.country = selectedEventCountry;
      if (activeCategory.l1) params.level1 = activeCategory.l1;
      if (activeCategory.l2) params.level2 = activeCategory.l2;
      if (lang) params.language = lang;

      const res = await axios.get(`${API_BASE_URL}/global-news`, {
        params,
        timeout: 20000,
      });
      if (requestVersion !== trendingRequestVersionRef.current) return;

      const rows = res.data?.news || [];
      const newTags = processNewsRows(rows, region, lang);

      // Merge with existing tags, deduplicating by lowercased title.
      if (isAppend) {
        setTrendingTags((prev) => {
          const merged = new Map();
          prev.forEach((t) => merged.set(t.title.toLowerCase().trim(), t));
          newTags.forEach((t) => {
            const key = t.title.toLowerCase().trim();
            if (!key) return;
            if (merged.has(key)) {
              const existing = merged.get(key);
              const existingIds = new Set(existing.videos.map((v) => v.video_id));
              t.videos.forEach((v) => {
                if (!existingIds.has(v.video_id)) existing.videos.push(v);
              });
            } else {
              merged.set(key, t);
            }
          });
          return sortTags(Array.from(merged.values()), region, lang);
        });
      } else {
        const dedupedMap = new Map();
        newTags.forEach((tag) => {
          const key = tag.title.toLowerCase().trim();
          if (!key) return;
          if (dedupedMap.has(key)) {
            const existing = dedupedMap.get(key);
            const existingIds = new Set(existing.videos.map((v) => v.video_id));
            tag.videos.forEach((v) => {
              if (!existingIds.has(v.video_id)) existing.videos.push(v);
            });
          } else {
            dedupedMap.set(key, tag);
          }
        });
        setTrendingTags(sortTags(Array.from(dedupedMap.values()), region, lang));
      }

      setHasMoreTrending(rows.length >= TRENDING_PAGE_SIZE);
    } catch (err) {
      if (requestVersion !== trendingRequestVersionRef.current) return;
      console.error("Error fetching global news:", err);
      if (!_retrying && !isAppend) {
        console.warn("[fetchGlobalNews] Retrying after error...");
        retryScheduled = true;
        trendingRetryTimerRef.current = window.setTimeout(() => {
          trendingRetryTimerRef.current = null;
          if (requestVersion === trendingRequestVersionRef.current) {
            fetchGlobalNews(region, lang, true, 0, requestVersion);
          }
        }, 2500);
        return;
      }
      if (!isAppend) setTrendingTags([]);
    } finally {
      if (requestVersion !== trendingRequestVersionRef.current || retryScheduled) return;
      if (isAppend) {
        setLoadingMoreTrending(false);
      } else {
        setLoadingMore(false);
      }
    }
  };

  const fetchVideos = async (
    countryCode,
    offset = 0,
    lat = null,
    lng = null,
    lang = null,
    _requestVersion = null,
  ) => {
    const isAppend = offset > 0;
    const requestVersion = _requestVersion ?? (
      isAppend ? videosRequestVersionRef.current : ++videosRequestVersionRef.current
    );
    if (requestVersion !== videosRequestVersionRef.current) return;

    setLoadingMore(true);
    try {
      const params = new URLSearchParams({
        offset: offset,
        limit: 12,
      });
      if (countryCode) params.append("origin_country", countryCode);
      if (lang) params.append("language", lang);
      if (lat != null && lng != null) {
        params.append("lat", lat);
        params.append("lng", lng);
      }

      const res = await axios.get(
        `${API_BASE_URL}/videos?${params.toString()}`,
        { timeout: 20000 },
      );
      if (requestVersion !== videosRequestVersionRef.current) return;

      const newVideos = res.data || [];
      setVideos((prev) => {
        const headlines = new Set(prev.map((v) => v.headline));
        const unique = newVideos.filter((v) => !headlines.has(v.headline));
        return [...prev, ...unique];
      });

      setHasMore(newVideos.length >= 12);
    } catch (error) {
      if (requestVersion !== videosRequestVersionRef.current) return;
      console.error("Error fetching videos:", error);
    } finally {
      if (requestVersion === videosRequestVersionRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  };

  // Fetch live TV channels — passes stream-type filters to the API.
  const fetchLiveChannels = async (region, hasIptv = false, hasYt = false) => {
    const queryKey = `${region || "Global"}|${hasIptv ? 1 : 0}|${hasYt ? 1 : 0}`;
    if (liveChannelsQueryKeyRef.current === queryKey) return;

    const requestVersion = ++liveRequestVersionRef.current;
    liveChannelsQueryKeyRef.current = queryKey;
    try {
      setLiveChannelsLoading(true);
      setLiveChannelsError(null);

      const params = { limit: 2000 };
      if (region && region !== "Global") params.region = region;
      if (hasIptv) params.has_iptv = true;
      if (hasYt) params.has_youtube = true;

      const res = await axios.get(`${API_BASE_URL}/live-news-channels`, {
        params,
        timeout: 20000,
      });
      if (requestVersion !== liveRequestVersionRef.current) return;

      const fetchedChannels = res.data?.channels || [];
      fetchedChannels.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setLiveChannels(fetchedChannels);
    } catch (e) {
      if (requestVersion !== liveRequestVersionRef.current) return;
      liveChannelsQueryKeyRef.current = "";
      console.error("Error fetching live TV channels:", e);
      setLiveChannelsError("Failed to load live TV channels");
      setLiveChannels([]);
    } finally {
      if (requestVersion === liveRequestVersionRef.current) {
        setLiveChannelsLoading(false);
      }
    }
  };

  // Load Live TV only when the Live tab is actually active. Avoid fetching and
  // parsing the full channel catalogue during the normal Headlines page load.
  useEffect(() => {
    if (activeTab !== "live_tv") return;
    fetchLiveChannels(selectedCountry, liveFilterIptv, liveFilterYt);
  }, [activeTab, selectedCountry, liveFilterIptv, liveFilterYt]);

  const queueScrollToTop = () => {
    pendingScrollToTopRef.current = true;
  };

  const invalidateTrendingRequests = () => {
    trendingRequestVersionRef.current += 1;
    if (trendingRetryTimerRef.current) {
      window.clearTimeout(trendingRetryTimerRef.current);
      trendingRetryTimerRef.current = null;
    }
  };

  const selectCountry = (countryCode) => {
    queueScrollToTop();
    invalidateTrendingRequests();
    videosRequestVersionRef.current += 1;
    liveRequestVersionRef.current += 1;
    liveChannelsQueryKeyRef.current = "";
    setLiveChannels([]);
    if (activeTab === "live_tv") setPlayingLiveChannel(null);
    setSelectedCountry(countryCode);
  };

  const selectTrendingCategory = (category) => {
    queueScrollToTop();
    invalidateTrendingRequests();
    setActiveCategory(category);
    handleTabSelect("trending");
  };

  const selectEventCountry = (countryCode) => {
    queueScrollToTop();
    invalidateTrendingRequests();
    setSelectedEventCountry(countryCode);
    handleTabSelect("trending");
  };

  const handleCountryChange = (e) => {
    selectCountry(e.target.value);
  };

  const selectLanguage = (language) => {
    queueScrollToTop();
    invalidateTrendingRequests();
    videosRequestVersionRef.current += 1;
    if (activeTab === "live_tv") setPlayingLiveChannel(null);
    setSelectedLanguage(language);
  };

  const handleTabSelect = (tab) => {
    if (!tab) return;
    // Invalidate work only when changing tabs. Re-clicking the already-active
    // tab must not orphan its in-flight request without triggering a new effect.
    if (tab !== activeTab) {
      invalidateTrendingRequests();
      videosRequestVersionRef.current += 1;
      liveRequestVersionRef.current += 1;
      liveChannelsQueryKeyRef.current = "";
    }
    setActiveTab(tab);
    // stop any playing youtube
    setActiveVideoKey(null);
    setPlayingRowId(null);
    if (tab !== "trending") {
      setActiveCategory({ l1: "", l2: "" });
    }
  };

  const goHome = (e) => {
    e?.preventDefault?.();
    queueScrollToTop();
    setPlayingLiveChannel(null);
    setActiveVideoKey(null);
    setPlayingRowId(null);
    setMobileHeroVideoId(null);
    setMobileHeroTag(null);
    setLiveSearch("");

    if (routeLocation.pathname !== "/") {
      navigate("/");
    }

    const onDefaultHome =
      activeTab === "trending" &&
      selectedCountry === "Global" &&
      selectedLanguage === "English" &&
      !activeCategory.l1 &&
      !activeCategory.l2 &&
      !selectedEventCountry;

    if (!onDefaultHome) {
      setSelectedEventCountry("");
      setActiveCategory({ l1: "", l2: "" });
      setSelectedCountry("Global");
      setSelectedLanguage("English");
      handleTabSelect("trending");
    } else if (routeLocation.pathname === "/") {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  };

  const openLivePage = () => {
    queueScrollToTop();
    setPlayingLiveChannel(null);
    handleTabSelect("live_tv");
    navigate("/");
  };

  const openLiveChannel = (channel) => {
    queueScrollToTop();
    setPlayingLiveChannel(channel);
    handleTabSelect("live_tv");
    navigate("/");
  };

  // Compute category tree only from rows that can actually be displayed. This keeps
  // users from selecting subcategories that immediately resolve to an empty page.
  const categoryTree = useMemo(() => {
    const tree = {};
    trendingTags
      .filter((tag) => Array.isArray(tag.videos) && tag.videos.length > 2)
      .filter((tag) => {
        if (!selectedEventCountry) return true;
        const selectedCode = String(selectedEventCountry).trim().toUpperCase();
        return Array.isArray(tag.countries) && tag.countries.some(
          (code) => String(code || "").trim().toUpperCase() === selectedCode,
        );
      })
      .forEach((tag) => {
        const { categoryL1, categoryL2 } = tag;
        if (!tree[categoryL1]) tree[categoryL1] = new Set();
        if (categoryL2) tree[categoryL1].add(categoryL2);
      });
    return tree;
  }, [trendingTags, selectedEventCountry]);

  const fetchEventCountries = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/event-countries`, { timeout: 10000 });
      const codes = res.data?.countries || [];
      const map = new Map();
      codes.forEach((code) => {
        if (code === "EU") return;
        const match = countries.find((c) => c.code === code);
        if (match) {
          map.set(code, match.name);
        }
      });
      const options = Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1])).map(([code, name]) => ({ code, name }));
      setEventCountryOptions(options);
    } catch (e) {
      console.error("Error fetching event countries", e);
    }
  };

  useEffect(() => {
    fetchEventCountries();
  }, []);

  const updateScrollButtons = () => {
    const newLeft = [];
    const newRight = [];
    rowRefs.current.forEach((row, index) => {
      if (row) {
        newLeft[index] = row.scrollLeft > 0;
        newRight[index] =
          row.scrollLeft + row.clientWidth < row.scrollWidth - 1;
      }
    });
    setCanScrollLeft(newLeft);
    setCanScrollRight(newRight);
  };

  useEffect(() => {
    updateScrollButtons();
  }, [videos]);

  const scrollRow = (rowIndex, direction) => {
    const row = rowRefs.current[rowIndex];
    if (row) {
      const scrollAmount = row.clientWidth * 0.8;
      const newScroll =
        direction === "left"
          ? row.scrollLeft - scrollAmount
          : row.scrollLeft + scrollAmount;
      row.scrollTo({ left: newScroll, behavior: "smooth" });
    }
  };

  useEffect(() => {
    const handleScroll = () => updateScrollButtons();
    rowRefs.current.forEach((row) => {
      if (row) row.addEventListener("scroll", handleScroll);
    });
    return () => {
      rowRefs.current.forEach((row) => {
        if (row) row.removeEventListener("scroll", handleScroll);
      });
    };
  }, [videos]);

  const rows = Array.from({ length: 5 }, (_, i) =>
    videos.slice(i * 100, (i + 1) * 100),
  );


  const [analysisModal, setAnalysisModal] = useState(null); // { title, outlet, raw_title, cachedAnalysis, cachedGeneratedAt, newsItemId, eventId, aiVideoUrl, videoCoverage }
  const openAnalysis = (title, outlet = "", raw_title = "", cachedAnalysis = "", newsItemId = null, aiVideoUrl = "", cachedGeneratedAt = null, videoCoverage = [], eventId = null) =>
    setAnalysisModal({ title, outlet, raw_title: raw_title || title, cachedAnalysis, cachedGeneratedAt, newsItemId, eventId, aiVideoUrl, videoCoverage });
  const closeAnalysis = () => setAnalysisModal(null);

  // ── Engagement state (likes, dislikes, comment counts) ──────────────────────
  // Key: news_item_id (string), value: { likes, dislikes, comments, user_reaction }
  const [engagement, setEngagement] = useState({});
  const [commentsModal, setCommentsModal] = useState(null); // { newsItemId, newsTitle, likes, dislikes } or null

  const openComments = React.useCallback((newsItemId, newsTitle) => {
    const eng = engagement[String(newsItemId)] || {};
    setCommentsModal({
      newsItemId,
      newsTitle,
      likes: eng.likes || 0,
      dislikes: eng.dislikes || 0,
      userReaction: eng.user_reaction || null,
    });
  }, [engagement]);

  const closeComments = React.useCallback(() => setCommentsModal(null), []);

  // Fetch bulk engagement data after trending tags are loaded
  const fetchEngagement = React.useCallback(async (tags) => {
    const ids = Array.from(new Set(tags.map((t) => t.id).filter(Boolean)));
    if (!ids.length) return;
    const userEmail = localStorage.getItem("dsstv_user_email") || "";

    // Backend intentionally caps bulk requests at 200 IDs. Infinite scrolling
    // can exceed that, so batch instead of silently turning later updates into 422s.
    const batches = [];
    for (let i = 0; i < ids.length; i += 200) batches.push(ids.slice(i, i + 200));

    try {
      const responses = await Promise.allSettled(batches.map((batch) => {
        const payload = { news_item_ids: batch };
        if (userEmail) payload.email = userEmail;
        return axios.post(`${API_BASE_URL}/engagement/bulk`, payload, { timeout: 10000 });
      }));
      const merged = {};
      responses.forEach((result) => {
        if (result.status === "fulfilled") {
          Object.assign(merged, result.value.data?.engagement || {});
        }
      });
      if (Object.keys(merged).length) {
        setEngagement((prev) => ({ ...prev, ...merged }));
      }
    } catch {
      // Silently fail — engagement counts just won't show.
    }
  }, []);

  // Handle email verification redirect: ?verified=true&email=xxx
  useEffect(() => {
    const params = new URLSearchParams(routeLocation.search);
    const verified = params.get("verified");
    const verifiedEmail = params.get("email");
    if (verified === "true" && verifiedEmail) {
      localStorage.setItem("dsstv_user_email", verifiedEmail.trim().toLowerCase());
      localStorage.setItem("dsstv_verified", "true");
      // Clean up URL without reloading
      window.history.replaceState({}, "", window.location.pathname);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isTrendingLoading = activeTab === "trending" && loadingMore;

  // Fetch engagement when trending tags update
  useEffect(() => {
    if (filteredTrendingTags.length > 0) {
      fetchEngagement(filteredTrendingTags);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredTrendingTags]);

  const appState = {
    navigate, selectedCountry, setSelectedCountry, selectedLanguage, setSelectedLanguage, videos, setVideos, playingLiveChannel, setPlayingLiveChannel, loading, setLoading, activeTab, setActiveTab, location, setLocation, zoom, setZoom, routeInfo, setRouteInfo, trendingTags, setTrendingTags, playingRowId, setPlayingRowId, offset, setOffset, loadingMore, setLoadingMore, hasMore, setHasMore, trendingOffset, setTrendingOffset, hasMoreTrending, setHasMoreTrending, loadingMoreTrending, setLoadingMoreTrending, TRENDING_PAGE_SIZE, activeCategory, setActiveCategory, selectedEventCountry, setSelectedEventCountry, eventCountryOptions, setEventCountryOptions, liveChannels, setLiveChannels, liveChannelsLoading, setLiveChannelsLoading, liveChannelsError, setLiveChannelsError, liveSearch, setLiveSearch, liveFilterIptv, setLiveFilterIptv, liveFilterYt, setLiveFilterYt, isMobileScreen, homeMainRef, footerRef, footerScrollHintVisible, footerHidden, headerSubfooterVisible, vipAfterHeroRef, mapRef, routingControlRef, rowRefs, canScrollLeft, setCanScrollLeft, canScrollRight, setCanScrollRight, hasMoved, setHasMoved, vipLastStableOverlapRef, vipHasInlinePlaybackRef, vipScheduleMeasureRef, filteredTrendingTags, extractYtId, sortTags, processNewsRows, fetchGlobalNews, fetchVideos, fetchLiveChannels, handleCountryChange, handleTabSelect, goHome, openLivePage, openLiveChannel, categoryTree, fetchEventCountries, rows, selectCountry, selectTrendingCategory, selectEventCountry, selectLanguage, openAnalysis, vipHeroImageLoaded, setVipHeroImageLoaded, isTrendingLoading,
    engagement, openComments
  };
  return (
    <>
      <ScrollToTop />
      <AppContext.Provider value={appState}>
      <ActiveVideoDispatchContext.Provider value={activeVideoDispatch}>
        <ActiveVideoStateContext.Provider value={activeVideoState}>
          <Routes>
            <Route element={<AppLayout />}>
              <Route index element={<HomeMain />} />
              <Route path="contact" element={<ContactPage />} />
              <Route path="terms" element={<TermsPage />} />
              <Route path="privacy" element={<PrivacyPage />} />
            </Route>
          </Routes>
          {analysisModal && (
            <AnalysisErrorBoundary
              key={analysisModal.newsItemId || analysisModal.title}
              onClose={closeAnalysis}
            >
              <React.Suspense fallback={<ModalChunkFallback label="Opening AI Analysis…" />}>
                <MediaAnalysisModal
                  title={analysisModal.title}
                  outlet={analysisModal.outlet}
                  cachedAnalysis={analysisModal.cachedAnalysis}
                  cachedGeneratedAt={analysisModal.cachedGeneratedAt}
                  newsItemId={analysisModal.newsItemId}
                  eventId={analysisModal.eventId}
                  aiVideoUrl={analysisModal.aiVideoUrl}
                  videoCoverage={analysisModal.videoCoverage}
                  onClose={closeAnalysis}
                />
              </React.Suspense>
            </AnalysisErrorBoundary>
          )}
          {commentsModal && (
            <React.Suspense fallback={<ModalChunkFallback label="Opening comments…" />}>
              <CommentsModal
                newsItemId={commentsModal.newsItemId}
                newsTitle={commentsModal.newsTitle}
                initialLikes={commentsModal.likes}
                initialDislikes={commentsModal.dislikes}
                initialUserReaction={commentsModal.userReaction}
                onClose={closeComments}
                onEngagementChange={(newsItemId, updates) => {
                  setEngagement((prev) => ({
                    ...prev,
                    [String(newsItemId)]: {
                      ...(prev[String(newsItemId)] || {}),
                      ...updates
                    }
                  }));
                }}
              />
            </React.Suspense>
          )}
        </ActiveVideoStateContext.Provider>
      </ActiveVideoDispatchContext.Provider>
      </AppContext.Provider>
    </>
  );
}

function AppLayout() {
  const {
    navigate, selectedCountry, setSelectedCountry, selectedLanguage, setSelectedLanguage, videos, setVideos, playingLiveChannel, setPlayingLiveChannel, loading, setLoading, activeTab, setActiveTab, location, setLocation, zoom, setZoom, routeInfo, setRouteInfo, trendingTags, setTrendingTags, playingRowId, setPlayingRowId, offset, setOffset, loadingMore, setLoadingMore, hasMore, setHasMore, trendingOffset, setTrendingOffset, hasMoreTrending, setHasMoreTrending, loadingMoreTrending, setLoadingMoreTrending, TRENDING_PAGE_SIZE, activeCategory, setActiveCategory, selectedEventCountry, setSelectedEventCountry, eventCountryOptions, setEventCountryOptions, liveChannels, setLiveChannels, liveChannelsLoading, setLiveChannelsLoading, liveChannelsError, setLiveChannelsError, liveSearch, setLiveSearch, liveFilterIptv, setLiveFilterIptv, liveFilterYt, setLiveFilterYt, isMobileScreen, homeMainRef, footerRef, footerScrollHintVisible, footerHidden, headerSubfooterVisible, vipAfterHeroRef, mapRef, routingControlRef, rowRefs, canScrollLeft, setCanScrollLeft, canScrollRight, setCanScrollRight, hasMoved, setHasMoved, vipLastStableOverlapRef, vipHasInlinePlaybackRef, vipScheduleMeasureRef, filteredTrendingTags, extractYtId, sortTags, processNewsRows, fetchGlobalNews, fetchVideos, fetchLiveChannels, handleCountryChange, handleTabSelect, goHome, openLivePage, openLiveChannel, categoryTree, fetchEventCountries, rows, selectCountry, selectTrendingCategory, selectEventCountry, selectLanguage, isTrendingLoading
  } = React.useContext(AppContext);

  const [menuOpen, setMenuOpen] = useState(false);
  const [openL1, setOpenL1] = useState(null);
  const [openHeadlines, setOpenHeadlines] = useState(false);
  const [openEventCountry, setOpenEventCountry] = useState(false);
  const [openCountry, setOpenCountry] = useState(false);
  const [openLanguage, setOpenLanguage] = useState(false);
  const [openLive, setOpenLive] = useState(false);

  const compactHeaderCountryName = (code, fallback = "") => {
    const compact = { US: "US", GB: "UK", AE: "UAE", EU: "EU" };
    if (compact[code]) return compact[code];
    return countries.find((c) => c.code === code)?.name || fallback;
  };

  return (
    <>
      {/* ================= NAVBAR ================= */}
      {!isTrendingLoading && <header className="ds-header-sticky">
      <nav className="ds-navbar">
        <Link
          to="/"
          className="ds-logo-link"
          aria-label="dataSCIsive home"
          onClick={(e) => {
            goHome(e);
            setMenuOpen(false);
          }}
        >
          <img src="/logo_1.webp" alt="dataSCIsive" className="ds-logo-img" />
        </Link>

        {/* TOGGLE BUTTON (MOBILE ONLY) */}
        <button className="ds-toggle-btn" onClick={(e) => { e.stopPropagation(); setMenuOpen(true); }}>
          ☰
        </button>

      <div className="ds-menu-wrap">
        <div className="d-flex align-items-center ds-header-gap ds-menu-primary">
          {/* Headlines Dropdown */}
          <div className="ds-dropdown ds-headlines-dropdown">
            <span className="ds-link">Headlines ▼</span>
            <div className="ds-dropdown-menu">
              <span
                className={`ds-dd-item fw-bold text-warning ${!activeCategory.l1 && !activeCategory.l2 ? "ds-dd-item--selected" : ""}`}
                onClick={() => selectTrendingCategory({ l1: "", l2: "" })}
              >
                All Categories
              </span>
              {Object.keys(categoryTree)
                .sort()
                .map((l1, idx) => {
                  const hasChildren = categoryTree[l1].size > 0;
                  const isOpen = openL1 === l1;

                  return (
                    <div
                      key={idx}
                      className="ds-dropdown ds-dropdown-nested"
                      style={{ position: "relative" }}
                    >
                      <span
                        className={`ds-dd-item d-flex justify-content-between align-items-center ${activeCategory.l1 === l1 && !activeCategory.l2 ? "ds-dd-item--selected" : ""}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          hasChildren
                            ? setOpenL1(isOpen ? null : l1)
                            : selectTrendingCategory({ l1, l2: "" });
                        }}
                      >
                        {l1}
                        {hasChildren && (
                          <span className={`arrow ${isOpen ? "open" : ""}`}>
                            ›
                          </span>
                        )}
                      </span>

                      {hasChildren && isOpen && (
                        <div className="ds-second-level-menu">
                          {[...categoryTree[l1]].sort().map((l2, vIdx) => (
                            <span
                              key={vIdx}
                              className={`ds-dd-item text-truncate ${activeCategory.l1 === l1 && activeCategory.l2 === l2 ? "ds-dd-item--selected" : ""}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                selectTrendingCategory({ l1, l2 });
                              }}
                            >
                              {l2}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>
        </div>

        {/* Event Country · Live · Global Country — Live centered between the two country dropdowns */}
        <div className="ds-menu-mid-trio ds-header-gap">
          <div className="ds-dropdown ds-menu-mid-trio__event">
            <span
              className="ds-link"
              title={eventCountryOptions.find((c) => c.code === selectedEventCountry)?.name || "Event Country"}
            >
              {compactHeaderCountryName(selectedEventCountry, "EVENT Country")} ▼
            </span>
            <div className="ds-dropdown-menu">
              <span className="ds-dd-item" onClick={() => selectEventCountry("")}>
                All Event Countries
              </span>
              {eventCountryOptions.map((c) => (
                <span key={c.code} className="ds-dd-item" onClick={() => selectEventCountry(c.code)}>
                  {c.name}
                </span>
              ))}
            </div>
          </div>

        {/* Live Dropdown */}
        <div
          className="ds-dropdown ds-menu-mid-trio__live"
          onMouseEnter={() => fetchLiveChannels(selectedCountry, liveFilterIptv, liveFilterYt)}
        >
          <span
            className="ds-link ds-live-dropdown-toggle"
            // onClick={openLivePage}
          >
            <span className="ds-live-indicator" aria-hidden="true" />
            <span className="ds-live-dropdown-toggle__left">Live</span>
            <span className="ds-live-dropdown-toggle__right" aria-hidden="true">
              <span className="ds-live-dropdown-toggle__caret">▼</span>
            </span>
          </span>
          <div
            className="ds-dropdown-menu"
            style={{ maxHeight: "400px", overflowY: "auto" }}
          >
            {/* <span
              className="ds-dd-item fw-bold text-muted"
              onClick={() => {
                setLiveSearch("");
                openLivePage();
              }}
            >
              All Channels
            </span> */}
            {(liveChannels || [])
              .filter((c) => {
                if (!selectedCountry || selectedCountry === "Global") return true;
                const countryObj = countries.find(
                  (co) => co.code === selectedCountry,
                );
                const countryName = countryObj ? countryObj.name : "";
                return (
                  c.country_code === selectedCountry ||
                  c.country === selectedCountry ||
                  c.country === countryName ||
                  (c.country && c.country.includes(selectedCountry))
                );
              })
              .filter((c) => liveChannelMatchesLanguage(c.language, selectedLanguage))
              .map((ch, idx) => {
                const name = ch.name || "Unknown";
                const fullCountryName = countries.find(co => co.code === ch.country_code)?.name || ch.country || ch.country_code || "";
                const langShort = ch.language ? ch.language.substring(0, 2).toUpperCase() : "";
                const meta = [fullCountryName, langShort].filter(Boolean);
                const display = meta.length > 0 ? `${name} (${meta.join('/')})` : name;
                return (
                  <span
                    key={`${name}-${idx}`}
                    className="ds-dd-item text-truncate"
                    title={display}
                    onClick={() => openLiveChannel(ch)}
                    style={{ fontSize: 12 }}
                  >
                    {display}
                  </span>
                );
              })}
          </div>
        </div>

          {/* Country Dropdown */}
          <div className="ds-dropdown ds-dropdown-right ds-menu-mid-trio__country">
            <span
              className="ds-link"
              title={countries.find((c) => c.code === selectedCountry)?.name || "Global"}
            >
              {compactHeaderCountryName(selectedCountry, "Global")} {" "}
              ▼
            </span>
            <div
              className="ds-dropdown-menu"
              style={{ maxHeight: "400px", overflowY: "auto" }}
            >
              {countries.map((c) => (
                <span
                  key={c.code}
                  className="ds-dd-item text-truncate"
                  onClick={() => selectCountry(c.code)}
                >
                  {c.name}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Language */}
        <div className="d-flex ds-header-gap pe-md-4 ds-menu-secondary">
          {/* Language Dropdown */}
          <div className="ds-dropdown ds-dropdown-right">
            <span className="ds-link">
              {selectedLanguage || "Language"} ▼
            </span>
            <div
              className="ds-dropdown-menu"
              style={{ maxHeight: "400px", overflowY: "auto" }}
            >
              <span
                className="ds-dd-item ds-dd-item--all"
                onClick={() => {
                  selectLanguage("");
                  // if (activeTab === "live_tv") {
                  //   setLiveSearch("");
                  // }
                }}
              >
                All Languages
              </span>
              {liveLanguages.map((lang) => (
                <span
                  key={lang}
                  className="ds-dd-item text-truncate"
                  onClick={() => {
                    selectLanguage(lang);
                    // if (activeTab === "live_tv") {
                    //   setLiveSearch(lang);
                    // }
                  }}
                >
                  {lang}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* MOBILE SIDEBAR */}
      <div className={`ds-sidebar ${menuOpen ? "open" : ""}`} onClick={(e) => e.stopPropagation()}>
        <button className="ds-close" onClick={(e) => { e.stopPropagation(); setMenuOpen(false); }}>
          ☰
        </button>

        <div className=" mobile-menu-wrap">
          {/* ===== HEADLINES ===== */}
          <div className="ds-dropdown">
            <span
              className="ds-link d-flex align-items-center justify-content-between"
              onClick={(e) => { e.stopPropagation(); setOpenHeadlines(!openHeadlines); }}
            >
              Headlines
              <span
                className={`ds-dropdown-icon ${openHeadlines ? "open" : ""}`}
              >
                ▾
              </span>
            </span>

            {openHeadlines && (
              <div className="ds-dropdown-menu">
                <span
                  className={`ds-dd-item fw-bold text-warning ${!activeCategory.l1 && !activeCategory.l2 ? "ds-dd-item--selected" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    selectTrendingCategory({ l1: "", l2: "" });
                    setMenuOpen(false);
                  }}
                >
                  All Categories
                </span>

                {Object.keys(categoryTree)
                  .sort()
                  .map((l1, idx) => {
                    const hasChildren = categoryTree[l1].size > 0;
                    const isOpen = openL1 === l1;

                    return (
                      <div key={idx} className="ds-category-block">
                        <span
                          className={`ds-dd-item d-flex justify-content-between align-items-center ${activeCategory.l1 === l1 && !activeCategory.l2 ? "ds-dd-item--selected" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (hasChildren) {
                              setOpenL1(isOpen ? null : l1);
                              return;
                            }
                            selectTrendingCategory({ l1, l2: "" });
                            setMenuOpen(false);
                          }}
                        >
                          {l1}
                          {hasChildren && (
                            <span className={`arrow ${isOpen ? "open" : ""}`}>
                              ›
                            </span>
                          )}
                        </span>

                        {hasChildren && isOpen && (
                          <div className="ds-second-level">
                            {[...categoryTree[l1]].sort().map((l2, vIdx) => (
                              <span
                                key={vIdx}
                                className={`ds-dd-item sub text-truncate ${activeCategory.l1 === l1 && activeCategory.l2 === l2 ? "ds-dd-item--selected" : ""}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  selectTrendingCategory({ l1, l2 });
                                  setMenuOpen(false);
                                }}
                              >
                                {l2}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}
          </div>

          {/* ===== LIVE ===== */}
          <div className="ds-dropdown mt-4">
            <span
              className="ds-link ds-live-dropdown-toggle ds-live-dropdown-toggle--full"
              onClick={(e) => {
                e.stopPropagation();
                const nextOpen = !openLive;
                setOpenLive(nextOpen);
                if (nextOpen) fetchLiveChannels(selectedCountry, liveFilterIptv, liveFilterYt);
              }}
            >
              <span className="ds-live-dropdown-toggle__label">
                <span className="ds-live-indicator" aria-hidden="true" />
                <span className="ds-live-dropdown-toggle__left">Live</span>
              </span>
              <span className="ds-live-dropdown-toggle__right" aria-hidden="true">
                <span
                  className={`ds-dropdown-icon ds-live-dropdown-toggle__caret ${openLive ? "open" : ""}`}
                >
                  ▾
                </span>
              </span>
            </span>

            {openLive && (
              <div className="ds-dropdown-menu" style={{ maxHeight: "300px", overflowY: "auto" }}>
                {/* <span
                  className="ds-dd-item fw-bold text-muted"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLiveSearch("");
                    openLivePage();
                    setMenuOpen(false);
                  }}
                >
                  All Channels
                </span> */}
                {(liveChannels || [])
                  .filter((c) => {
                    if (!selectedCountry || selectedCountry === "Global") return true;
                    const countryObj = countries.find(
                      (co) => co.code === selectedCountry,
                    );
                    const countryName = countryObj ? countryObj.name : "";
                    return (
                      c.country_code === selectedCountry ||
                      c.country === selectedCountry ||
                      c.country === countryName ||
                      (c.country && c.country.includes(selectedCountry))
                    );
                  })
                  .filter((c) => liveChannelMatchesLanguage(c.language, selectedLanguage))
                  .map((ch, idx) => {
                    const name = ch.name || "Unknown";
                    const fullCountryName = countries.find(co => co.code === ch.country_code)?.name || ch.country || ch.country_code || "";
                    const langShort = ch.language ? ch.language.substring(0, 2).toUpperCase() : "";
                    const meta = [fullCountryName, langShort].filter(Boolean);
                    const display = meta.length > 0 ? `${name} (${meta.join('/')})` : name;
                    return (
                      <span
                        key={`${name}-${idx}`}
                        className="ds-dd-item text-truncate"
                        title={display}
                        style={{ fontSize: 12 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          openLiveChannel(ch);
                          setMenuOpen(false);
                        }}
                      >
                        {display}
                      </span>
                    );
                  })}
              </div>
            )}
          </div>

          <div className="ds-dropdown mt-4">
            <span className="ds-link d-flex align-items-center justify-content-between" onClick={(e) => { e.stopPropagation(); setOpenEventCountry(!openEventCountry); }}>
              EVENT Country
              <span className={`ds-dropdown-icon ${openEventCountry ? "open" : ""}`}>
                ▾
              </span>
            </span>

            {openEventCountry && (
              <div className="ds-dropdown-menu">
                <span className="ds-dd-item ds-dd-item--all" onClick={(e) => { e.stopPropagation(); selectEventCountry(""); setMenuOpen(false); }}>
                  All Event Countries
                </span>
                {eventCountryOptions.map((c) => (
                  <span key={c.code} className="ds-dd-item" onClick={(e) => { e.stopPropagation(); selectEventCountry(c.code); setMenuOpen(false); }}>
                    {c.name}
                  </span>
                ))}
              </div>
            )}
          </div>
          {/* ===== COUNTRY ===== */}
          <div className="ds-dropdown mt-4">
            <span
              className="ds-link d-flex align-items-center justify-content-between"
              onClick={(e) => { e.stopPropagation(); setOpenCountry(!openCountry); }}
            >
              {countries.find((c) => c.code === selectedCountry)?.name ||
                "United States"}
              <span
                className={`ds-dropdown-icon ${openCountry ? "open" : ""}`}
              >
                ▾
              </span>
            </span>

            {openCountry && (
              <div className="ds-dropdown-menu">
                {countries.map((c) => (
                  <span
                    key={c.code}
                    className="ds-dd-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      selectCountry(c.code);
                      setMenuOpen(false);
                    }}
                  >
                    {c.name}
                  </span>
                ))}
              </div>
            )}
          </div>
          {/* ===== LANGUAGE ===== */}
          <div className="ds-dropdown mt-4">
            <span
              className="ds-link d-flex align-items-center justify-content-between"
              onClick={(e) => { e.stopPropagation(); setOpenLanguage(!openLanguage); }}
            >
              {selectedLanguage || "All Languages"}
              <span
                className={`ds-dropdown-icon ${openLanguage ? "open" : ""}`}
              >
                ▾
              </span>
            </span>

            {openLanguage && (
              <div className="ds-dropdown-menu" style={{ maxHeight: "300px", overflowY: "auto" }}>
                <span
                  className="ds-dd-item ds-dd-item--all"
                  onClick={(e) => {
                    e.stopPropagation();
                    selectLanguage("");
                    // if (activeTab === "live_tv") {
                    //   setLiveSearch("");
                    // }
                    setMenuOpen(false);
                  }}
                >
                  All Languages
                </span>
                {liveLanguages.map((lang) => (
                  <span
                    key={lang}
                    className="ds-dd-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      selectLanguage(lang);
                      // if (activeTab === "live_tv") {
                      //   setLiveSearch(lang);
                      // }
                      setMenuOpen(false);
                    }}
                  >
                    {lang}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* OVERLAY */}
      {menuOpen && (
        <div className="ds-overlay" onClick={() => setMenuOpen(false)} />
      )}
      </nav>
      <div
        className={`ds-header-subfooter${headerSubfooterVisible ? " ds-header-subfooter--visible" : ""}`}
        aria-hidden="true"
      />
      </header>}

      <Outlet />
      {!isTrendingLoading && <footer
        ref={footerRef}
        className={`ds-footer${footerScrollHintVisible ? "" : " ds-footer--scroll-end"}${footerHidden ? " ds-footer--hidden" : ""}`}
      >
        <div className="ds-footer-content">
          <div className="ds-footer-right">
            <Link to="/terms">Terms of Use</Link>
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/contact">Contact</Link>
          </div>
        </div>
      </footer>}
    </>
  );
}


function HomeMain() {
  const {
    navigate, selectedCountry, setSelectedCountry, selectedLanguage, setSelectedLanguage, videos, setVideos, playingLiveChannel, setPlayingLiveChannel, loading, setLoading, activeTab, setActiveTab, location, setLocation, zoom, setZoom, routeInfo, setRouteInfo, trendingTags, setTrendingTags, playingRowId, setPlayingRowId, offset, setOffset, loadingMore, setLoadingMore, hasMore, setHasMore, trendingOffset, setTrendingOffset, hasMoreTrending, setHasMoreTrending, loadingMoreTrending, setLoadingMoreTrending, TRENDING_PAGE_SIZE, activeCategory, setActiveCategory, selectedEventCountry, setSelectedEventCountry, eventCountryOptions, setEventCountryOptions, liveChannels, setLiveChannels, liveChannelsLoading, setLiveChannelsLoading, liveChannelsError, setLiveChannelsError, liveSearch, setLiveSearch, liveFilterIptv, setLiveFilterIptv, liveFilterYt, setLiveFilterYt, isMobileScreen, homeMainRef, footerRef, footerHidden, vipAfterHeroRef, mapRef, routingControlRef, rowRefs, canScrollLeft, setCanScrollLeft, canScrollRight, setCanScrollRight, hasMoved, setHasMoved, vipLastStableOverlapRef, vipHasInlinePlaybackRef, vipScheduleMeasureRef, filteredTrendingTags, extractYtId, sortTags, processNewsRows, fetchGlobalNews, fetchVideos, fetchLiveChannels, handleCountryChange, handleTabSelect, openLivePage, openLiveChannel, categoryTree, fetchEventCountries, rows, openAnalysis, vipHeroImageLoaded, setVipHeroImageLoaded
  } = React.useContext(AppContext);
  const { mobileHeroVideoId, activeVideoKey, mobileHeroTag } = React.useContext(ActiveVideoStateContext);
  const { setMobileHeroVideoId, setMobileHeroTag } = React.useContext(ActiveVideoDispatchContext);
  const vipHeroDesktopTitleRef = useRef(null);
  const mobileHeroPlayerRef = useRef(null);
  const mobileHeroYouTubePlayerRef = useRef(null);
  const mobileHeroControlsHideTimerRef = useRef(null);
  const mobileHeroAutoPreviewStartTimerRef = useRef(null);
  const mobileHeroAutoPreviewTimerRef = useRef(null);
  const mobileHeroIframeBlurTimerRef = useRef(null);
  const mobileHeroLastIframeSignalAtRef = useRef(0);
  const homepageAiHintInitializedRef = useRef(false);
  const mobileHeroAutoPreviewStartedRef = useRef(false);
  const [isMobileHeroFullscreen, setIsMobileHeroFullscreen] = useState(false);
  const [isMobileHeroPseudoFullscreen, setIsMobileHeroPseudoFullscreen] = useState(false);
  const [mobileHeroIframeNode, setMobileHeroIframeNode] = useState(null);
  const [isMobileHeroPlayerPlaying, setIsMobileHeroPlayerPlaying] = useState(false);
  const [isMobileHeroFullscreenControlVisible, setIsMobileHeroFullscreenControlVisible] = useState(false);
  const [showHomepageAiHint, setShowHomepageAiHint] = useState(false);
  const [isMobileHeroAutoPreview, setIsMobileHeroAutoPreview] = useState(false);
  const [isMobileHeroAiTapEnabled, setIsMobileHeroAiTapEnabled] = useState(!isMobileScreen);
  const isMobileHeroFullscreenActive = isMobileHeroFullscreen || isMobileHeroPseudoFullscreen;

  const firstTrendingTag = filteredTrendingTags[0];
  const remainingTrendingTags = filteredTrendingTags.slice(1);
  const currentHeroTag = (isMobileScreen && mobileHeroTag) ? mobileHeroTag : firstTrendingTag;
  const heroTitle = currentHeroTag?.title;
  const firstRowFirstVideoId = firstTrendingTag?.videos?.[0]?.video_id || null;

  const clearMobileHeroAutoPreviewStartTimer = React.useCallback(() => {
    if (typeof window === "undefined") return;
    if (mobileHeroAutoPreviewStartTimerRef.current) {
      window.clearTimeout(mobileHeroAutoPreviewStartTimerRef.current);
      mobileHeroAutoPreviewStartTimerRef.current = null;
    }
  }, []);

  const clearMobileHeroAutoPreviewTimer = React.useCallback(() => {
    if (typeof window === "undefined") return;
    if (mobileHeroAutoPreviewTimerRef.current) {
      window.clearTimeout(mobileHeroAutoPreviewTimerRef.current);
      mobileHeroAutoPreviewTimerRef.current = null;
    }
  }, []);

  const closeMobileHeroPlayer = React.useCallback(() => {
    clearMobileHeroAutoPreviewStartTimer();
    clearMobileHeroAutoPreviewTimer();
    mobileHeroYouTubePlayerRef.current = null;
    mobileHeroLastIframeSignalAtRef.current = 0;
    setMobileHeroIframeNode(null);
    setIsMobileHeroPseudoFullscreen(false);
    setIsMobileHeroPlayerPlaying(false);
    setIsMobileHeroFullscreenControlVisible(false);
    setIsMobileHeroAutoPreview(false);
    setMobileHeroVideoId(null);
    setMobileHeroTag(null);
  }, [clearMobileHeroAutoPreviewStartTimer, clearMobileHeroAutoPreviewTimer, setMobileHeroTag, setMobileHeroVideoId]);

  const scheduleMobileHeroAutoPreviewClose = React.useCallback(() => {
    if (typeof window === "undefined" || !isMobileHeroAutoPreview) return;
    clearMobileHeroAutoPreviewTimer();
    mobileHeroAutoPreviewTimerRef.current = window.setTimeout(() => {
      closeMobileHeroPlayer();
    }, MOBILE_HERO_AUTOPREVIEW_DURATION_MS);
  }, [clearMobileHeroAutoPreviewTimer, closeMobileHeroPlayer, isMobileHeroAutoPreview]);


  useEffect(() => {
    if (
      typeof window === "undefined" ||
      !heroTitle ||
      (isMobileScreen && !isMobileHeroAiTapEnabled) ||
      homepageAiHintInitializedRef.current
    ) {
      return undefined;
    }

    homepageAiHintInitializedRef.current = true;

    setShowHomepageAiHint(true);
    const hideTimerId = window.setTimeout(() => {
      setShowHomepageAiHint(false);
    }, HOMEPAGE_AI_HINT_TOTAL_MS);

    return () => {
      window.clearTimeout(hideTimerId);
    };
  }, [heroTitle, isMobileHeroAiTapEnabled, isMobileScreen]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const syncMobileHeroFullscreen = () => {
      const fullscreenElement =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        null;
      const host = mobileHeroPlayerRef.current;
      setIsMobileHeroFullscreen(Boolean(host && fullscreenElement && (fullscreenElement === host || host.contains(fullscreenElement))));
    };

    syncMobileHeroFullscreen();
    document.addEventListener("fullscreenchange", syncMobileHeroFullscreen);
    document.addEventListener("webkitfullscreenchange", syncMobileHeroFullscreen);

    return () => {
      document.removeEventListener("fullscreenchange", syncMobileHeroFullscreen);
      document.removeEventListener("webkitfullscreenchange", syncMobileHeroFullscreen);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    return () => {
      if (mobileHeroAutoPreviewStartTimerRef.current) {
        window.clearTimeout(mobileHeroAutoPreviewStartTimerRef.current);
        mobileHeroAutoPreviewStartTimerRef.current = null;
      }
      if (mobileHeroAutoPreviewTimerRef.current) {
        window.clearTimeout(mobileHeroAutoPreviewTimerRef.current);
        mobileHeroAutoPreviewTimerRef.current = null;
      }
      if (mobileHeroControlsHideTimerRef.current) {
        window.clearTimeout(mobileHeroControlsHideTimerRef.current);
        mobileHeroControlsHideTimerRef.current = null;
      }
      if (mobileHeroIframeBlurTimerRef.current) {
        window.clearTimeout(mobileHeroIframeBlurTimerRef.current);
        mobileHeroIframeBlurTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    if (!isMobileScreen || !mobileHeroVideoId) {
      if (mobileHeroControlsHideTimerRef.current) {
        window.clearTimeout(mobileHeroControlsHideTimerRef.current);
        mobileHeroControlsHideTimerRef.current = null;
      }
      if (mobileHeroIframeBlurTimerRef.current) {
        window.clearTimeout(mobileHeroIframeBlurTimerRef.current);
        mobileHeroIframeBlurTimerRef.current = null;
      }
      clearMobileHeroAutoPreviewStartTimer();
      clearMobileHeroAutoPreviewTimer();
      mobileHeroYouTubePlayerRef.current = null;
      mobileHeroLastIframeSignalAtRef.current = 0;
      setMobileHeroIframeNode(null);
      setIsMobileHeroPseudoFullscreen(false);
      setIsMobileHeroPlayerPlaying(false);
      setIsMobileHeroFullscreenControlVisible(false);
      setIsMobileHeroAutoPreview(false);
    }

    return undefined;
  }, [clearMobileHeroAutoPreviewStartTimer, clearMobileHeroAutoPreviewTimer, isMobileScreen, mobileHeroVideoId]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    if (!isMobileScreen) {
      clearMobileHeroAutoPreviewStartTimer();
      setIsMobileHeroAiTapEnabled(true);
      return undefined;
    }

    if (
      activeTab !== "trending" ||
      !firstTrendingTag ||
      !firstRowFirstVideoId
    ) {
      clearMobileHeroAutoPreviewStartTimer();
      return undefined;
    }

    if (mobileHeroAutoPreviewStartedRef.current) {
      clearMobileHeroAutoPreviewStartTimer();
      setIsMobileHeroAiTapEnabled(!isMobileHeroAutoPreview);
      return undefined;
    }

    if (mobileHeroVideoId || activeVideoKey) {
      clearMobileHeroAutoPreviewStartTimer();
      setIsMobileHeroAiTapEnabled(true);
      return undefined;
    }

    setIsMobileHeroAiTapEnabled(false);
    clearMobileHeroAutoPreviewStartTimer();
    mobileHeroAutoPreviewStartTimerRef.current = window.setTimeout(() => {
      mobileHeroAutoPreviewStartedRef.current = true;
      setIsMobileHeroAutoPreview(true);
      setMobileHeroVideoId(firstRowFirstVideoId);
      setMobileHeroTag(firstTrendingTag);
      window.scrollTo({ top: 0, behavior: "auto" });
      const trendingScroll = document.querySelector(".trending-scroll");
      trendingScroll?.scrollTo({ top: 0, behavior: "auto" });
      mobileHeroAutoPreviewStartTimerRef.current = null;
    }, MOBILE_HERO_AUTOPREVIEW_START_DELAY_MS);

    return () => {
      clearMobileHeroAutoPreviewStartTimer();
    };
  }, [
    activeTab,
    activeVideoKey,
    clearMobileHeroAutoPreviewStartTimer,
    firstRowFirstVideoId,
    firstTrendingTag,
    isMobileHeroAiTapEnabled,
    isMobileHeroAutoPreview,
    isMobileScreen,
    mobileHeroVideoId,
    setMobileHeroTag,
    setMobileHeroVideoId,
  ]);

  useEffect(() => {
    if (!isMobileHeroAutoPreview) {
      clearMobileHeroAutoPreviewTimer();
      return undefined;
    }

    if (
      !mobileHeroVideoId ||
      mobileHeroVideoId !== firstRowFirstVideoId ||
      mobileHeroTag !== firstTrendingTag
    ) {
      clearMobileHeroAutoPreviewTimer();
      setIsMobileHeroAutoPreview(false);
    }

    return undefined;
  }, [
    clearMobileHeroAutoPreviewTimer,
    firstRowFirstVideoId,
    firstTrendingTag,
    isMobileHeroAutoPreview,
    mobileHeroTag,
    mobileHeroVideoId,
  ]);

  const handleHeroAnalysisOpen = React.useCallback(() => {
    if (isMobileScreen && !isMobileHeroAiTapEnabled) {
      return;
    }
    // Prefer pre-computed 29-metric analysis_json; fall back to legacy ai_analysis text
    const heroCached = currentHeroTag?.analysis_json
      ? (typeof currentHeroTag.analysis_json === "string" ? currentHeroTag.analysis_json : JSON.stringify(currentHeroTag.analysis_json))
      : (currentHeroTag?.ai_analysis || "");
    openAnalysis(
      currentHeroTag?.title || "",
      "",
      currentHeroTag?.raw_title || "",
      heroCached,
      currentHeroTag?.id || null,
      currentHeroTag?.ai_video_url || "",
      currentHeroTag?.analysis_generated_at || null,
      currentHeroTag?.videos || []
    );
  }, [currentHeroTag, isMobileHeroAiTapEnabled, isMobileScreen, openAnalysis]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    if (mobileHeroControlsHideTimerRef.current) {
      window.clearTimeout(mobileHeroControlsHideTimerRef.current);
      mobileHeroControlsHideTimerRef.current = null;
    }

    if (!isMobileScreen || !mobileHeroVideoId) {
      setIsMobileHeroFullscreenControlVisible(false);
      return undefined;
    }

    if (!isMobileHeroPlayerPlaying) {
      setIsMobileHeroFullscreenControlVisible(true);
      return undefined;
    }

    if (!isMobileHeroFullscreenControlVisible) {
      return undefined;
    }

    mobileHeroControlsHideTimerRef.current = window.setTimeout(() => {
      setIsMobileHeroFullscreenControlVisible(false);
      mobileHeroControlsHideTimerRef.current = null;
    }, MOBILE_HERO_CONTROLS_HIDE_DELAY_MS);

    return () => {
      if (mobileHeroControlsHideTimerRef.current) {
        window.clearTimeout(mobileHeroControlsHideTimerRef.current);
        mobileHeroControlsHideTimerRef.current = null;
      }
    };
  }, [isMobileHeroFullscreenControlVisible, isMobileHeroPlayerPlaying, isMobileScreen, mobileHeroVideoId]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const body = document.body;
    const html = document.documentElement;
    const previousBodyOverflow = body.style.overflow;
    const previousHtmlOverflow = html.style.overflow;

    if (!isMobileHeroPseudoFullscreen) {
      body.classList.remove("mobile-hero-pseudo-fullscreen");
      return undefined;
    }

    body.classList.add("mobile-hero-pseudo-fullscreen");
    body.style.overflow = "hidden";
    html.style.overflow = "hidden";

    return () => {
      body.classList.remove("mobile-hero-pseudo-fullscreen");
      body.style.overflow = previousBodyOverflow;
      html.style.overflow = previousHtmlOverflow;
    };
  }, [isMobileHeroPseudoFullscreen]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (!isMobileHeroPseudoFullscreen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsMobileHeroPseudoFullscreen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isMobileHeroPseudoFullscreen]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return undefined;
    if (!isMobileScreen || !mobileHeroVideoId || !mobileHeroIframeNode) return undefined;

    const releaseIframeFocus = () => {
      if (mobileHeroIframeBlurTimerRef.current) {
        window.clearTimeout(mobileHeroIframeBlurTimerRef.current);
      }

      mobileHeroIframeBlurTimerRef.current = window.setTimeout(() => {
        try {
          mobileHeroIframeNode.blur?.();
        } catch {}
        try {
          mobileHeroPlayerRef.current?.focus?.({ preventScroll: true });
        } catch {
          try {
            mobileHeroPlayerRef.current?.focus?.();
          } catch {}
        }
        mobileHeroIframeBlurTimerRef.current = null;
      }, 140);
    };

    const syncControlVisibilityFromTap = () => {
      const now = Date.now();
      if (now - mobileHeroLastIframeSignalAtRef.current < 250) return;
      mobileHeroLastIframeSignalAtRef.current = now;

      setIsMobileHeroFullscreenControlVisible((visible) => (
        isMobileHeroPlayerPlaying ? !visible : true
      ));
      releaseIframeFocus();
    };

    const handleIframeFocus = () => {
      syncControlVisibilityFromTap();
    };

    const handleWindowBlur = () => {
      window.setTimeout(() => {
        if (document.activeElement === mobileHeroIframeNode) {
          syncControlVisibilityFromTap();
        }
      }, 0);
    };

    mobileHeroIframeNode.addEventListener("focus", handleIframeFocus);
    window.addEventListener("blur", handleWindowBlur);

    return () => {
      mobileHeroIframeNode.removeEventListener("focus", handleIframeFocus);
      window.removeEventListener("blur", handleWindowBlur);
    };
  }, [isMobileScreen, mobileHeroVideoId, mobileHeroIframeNode, isMobileHeroPlayerPlaying]);

  const toggleMobileHeroFullscreen = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (typeof document === "undefined") return;
    const host = mobileHeroPlayerRef.current;
    if (!host) return;

    if (isAppleMobileDevice()) {
      setIsMobileHeroPseudoFullscreen((current) => !current);
      return;
    }

    const fullscreenElement =
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      null;

    try {
      if (fullscreenElement) {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        }
        return;
      }

      if (host.requestFullscreen) {
        await host.requestFullscreen();
      } else if (host.webkitRequestFullscreen) {
        host.webkitRequestFullscreen();
      }
    } catch (error) {
      console.error("Mobile hero fullscreen failed", error);
    }
  };

  useLayoutEffect(() => {
    const main = homeMainRef.current;
    const afterHeroNode = vipAfterHeroRef.current;
    if (!main || typeof window === "undefined") return;

    let frameId = 0;
    let resizeObserver;

    const measureAndApply = () => {
      const el = vipHeroDesktopTitleRef.current;
      if (
        activeTab !== "trending" ||
        loadingMore ||
        window.innerWidth <= HEADLINE_MOBILE_BREAKPOINT ||
        !el ||
        !heroTitle
      ) {
        main.style.removeProperty("--vip-hero-desktop-top");
        afterHeroNode?.style.removeProperty("--vip-hero-desktop-top");
        return;
      }

      const textEl = el.querySelector(".vip-hero-title__text");
      const topPx = vipDesktopHeroTopPx(el, textEl || el, main);
      main.style.setProperty("--vip-hero-desktop-top", `${topPx}px`);
      afterHeroNode?.style.setProperty("--vip-hero-desktop-top", `${topPx}px`);
    };

    const schedule = () => {
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(() => {
        frameId = window.requestAnimationFrame(() => {
          measureAndApply();
          const el = vipHeroDesktopTitleRef.current;
          if (
            resizeObserver &&
            el &&
            activeTab === "trending" &&
            !loadingMore &&
            window.innerWidth > HEADLINE_MOBILE_BREAKPOINT &&
            heroTitle
          ) {
            resizeObserver.disconnect();
            resizeObserver.observe(el);
            const textEl = el.querySelector(".vip-hero-title__text");
            if (textEl) resizeObserver.observe(textEl);
            if (el.parentElement) resizeObserver.observe(el.parentElement);
            if (afterHeroNode) resizeObserver.observe(afterHeroNode);
          } else if (resizeObserver) {
            resizeObserver.disconnect();
          }
        });
      });
    };

    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(schedule);
    }

    schedule();
    window.addEventListener("resize", schedule);

    document.fonts?.ready?.then(schedule).catch(() => {});

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", schedule);
      resizeObserver?.disconnect();
      main.style.removeProperty("--vip-hero-desktop-top");
      afterHeroNode?.style.removeProperty("--vip-hero-desktop-top");
    };
  }, [activeTab, heroTitle, homeMainRef, loadingMore, vipAfterHeroRef, vipHeroImageLoaded]);

  const heroVideo = filteredTrendingTags[0]?.videos[0];
  const heroId = heroVideo?.video_id;
  const isTrendingLoading = loadingMore;
  const shouldLockMobileVipHero = isMobileScreen && Boolean(heroId);

  return (
    <div
      ref={homeMainRef}
      className={`netflix-app container-fluid pb-2 main-container app-main-with-footer${footerHidden ? " app-main-with-footer--footer-hidden" : ""}`}
    >
      {/* ================= MAIN CONTENT ================= */}
      {/* ================= TRENDING ================= */}
      {activeTab === "trending" && (
        <div
          className={`trending-scroll fancy-scroll ${
            shouldLockMobileVipHero
              ? "trending-scroll--vip-mobile-playing"
              : ""
          }`}
        >
          {isTrendingLoading && (
            <div className="premium-loader-container">
              <img 
                src="/dataSCIsiveTV_page_match_transparent_cleaned_v2.webp" 
                alt="Loading..." 
                className="premium-loader-img"
              />
              <div className="loading-text" aria-label="ONE EVENT MANY OUTLETS SIDE BY SIDE">
                <span className="loading-text-line loading-text-line--1">ONE EVENT</span>
                <span className="loading-text-line loading-text-line--2">MANY OUTLETS</span>
                <span className="loading-text-line loading-text-line--3">SIDE BY SIDE</span>
              </div>
            </div>
          )}

          {filteredTrendingTags.length === 0 && !loadingMore && (
            <div
              className="d-flex flex-column justify-content-center align-items-center text-secondary"
              style={{ minHeight: "50vh" }}
            >
              <h4 className="text-muted">No recent headlines match these filters</h4>
              <p>Try another category or event country.</p>
              {(activeCategory.l1 || activeCategory.l2 || selectedEventCountry) && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-light mt-2"
                  onClick={() => {
                    setActiveCategory({ l1: "", l2: "" });
                    setSelectedEventCountry("");
                  }}
                >
                  Clear headline filters
                </button>
              )}
            </div>
          )}

          {filteredTrendingTags.length > 0 && (
            <>
                {(() => {
                  const playingId =
                    isMobileScreen && mobileHeroVideoId
                      ? mobileHeroVideoId
                      : null;
                  const isMobileHeroPlaying =
                    isMobileScreen && Boolean(playingId);

                  return (
                    <div
                      className={
                        isMobileHeroPlaying
                          ? "vip-hero vip-hero--is-playing"
                          : "vip-hero"
                      }
                    >
                       {isMobileHeroPlaying ? (
                        <div
                          ref={mobileHeroPlayerRef}
                          className={`vip-hero-player-inner${isMobileHeroPseudoFullscreen ? " is-pseudo-fullscreen" : ""}`}
                          tabIndex={-1}
                        >
                          <YouTube
                            videoId={playingId}
                            opts={{
                              width: "100%",
                              height: "100%",
                              playerVars: {
                                autoplay: 1,
                                mute: isMobileHeroAutoPreview ? 1 : 0,
                                playsinline: 1,
                                rel: 0,
                                modestbranding: 1,
                                enablejsapi: 1,
                                fs: 0,
                              },
                            }}
                            onReady={(e) => {
                              try {
                                mobileHeroYouTubePlayerRef.current = e?.target;
                                setMobileHeroIframeNode(e?.target?.getIframe?.() || null);
                                if (isMobileHeroAutoPreview) {
                                  e?.target?.mute?.();
                                  scheduleMobileHeroAutoPreviewClose();
                                } else {
                                e?.target?.unMute?.();
                                e?.target?.setVolume?.(100);
                                }
                                const playPromise = e?.target?.playVideo?.();
                                if (playPromise && playPromise.catch) playPromise.catch(() => {});
                              } catch { }
                            }}
                            onStateChange={(e) => {
                              const state = e?.data;
                              const Y = window.YT?.PlayerState;
                              const isNowPlaying = Y ? state === Y.PLAYING : state === 1;
                              const isPaused = Y ? state === Y.PAUSED : state === 2;
                              const isEnded = Y ? state === Y.ENDED : state === 0;

                              if (isNowPlaying) {
                                setIsMobileHeroPlayerPlaying(true);
                                setIsMobileHeroFullscreenControlVisible(true);
                                if (isMobileHeroAutoPreview) {
                                  scheduleMobileHeroAutoPreviewClose();
                                }
                              } else if (isPaused) {
                                setIsMobileHeroPlayerPlaying(false);
                                setIsMobileHeroFullscreenControlVisible(true);
                              } else if (isEnded) {
                                setIsMobileHeroPlayerPlaying(false);
                                setIsMobileHeroFullscreenControlVisible(false);
                              }
                            }}
                            onEnd={() => {
                              closeMobileHeroPlayer();
                            }}
                            className="vip-hero-player-iframe-wrapper"
                            iframeClassName="vip-hero-player-iframe"
                          />
                          <button
                            type="button"
                            className={`vip-hero-fullscreen-btn${isMobileHeroFullscreenActive ? " is-active" : ""}${isMobileHeroFullscreenControlVisible ? " is-visible" : ""}`}
                            onClick={toggleMobileHeroFullscreen}
                            aria-label={isMobileHeroFullscreenActive ? "Exit fullscreen" : "Enter fullscreen"}
                            title={isMobileHeroFullscreenActive ? "Exit fullscreen" : "Enter fullscreen"}
                          >
                            <svg
                              viewBox="0 0 24 24"
                              aria-hidden="true"
                              focusable="false"
                            >
                              {isMobileHeroFullscreenActive ? (
                                <path
                                  d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"
                                  fill="currentColor"
                                />
                              ) : (
                                <path
                                  d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zm-3-12v2h3v3h2V5h-5z"
                                  fill="currentColor"
                                />
                              )}
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <>
                          <img
                            key={heroId}
                            className="vip-hero-bg"
                            src={`https://img.youtube.com/vi/${heroId}/maxresdefault.jpg`}
                            fetchPriority="high"
                            decoding="async"
                            onLoad={(e) => {
                              if (e.target.src.includes('maxresdefault.jpg') && e.target.naturalWidth === 120) {
                                e.target.src = heroVideo?.thumbnail || `https://img.youtube.com/vi/${heroId}/hqdefault.jpg`;
                              } else {
                                setVipHeroImageLoaded(true);
                                if (vipScheduleMeasureRef.current) {
                                  vipScheduleMeasureRef.current();
                                }
                              }
                            }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = heroVideo?.thumbnail || `https://img.youtube.com/vi/${heroId}/hqdefault.jpg`;
                              setVipHeroImageLoaded(true);
                              if (vipScheduleMeasureRef.current) {
                                vipScheduleMeasureRef.current();
                              }
                            }}
                            alt="hero-thumbnail"
                          />
                          {isMobileScreen && heroId && (
                            <div
                              className="vip-hero-play-btn-overlay"
                              onClick={() => {
                                clearMobileHeroAutoPreviewTimer();
                                setIsMobileHeroAutoPreview(false);
                                setMobileHeroVideoId(heroId);
                                setMobileHeroTag(firstTrendingTag);
                              }}
                            >
                              <div className="vip-hero-play-icon" aria-hidden="true">
                                <span className="play-glyph" />
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {!isMobileHeroPlaying && (
                        <div className="vip-hero-content vip-hero-content--mobile">
                          {isMobileScreen && showHomepageAiHint && <HeroAiHint />}
                          <h2
                            className={`vip-hero-title vip-hero-title--clickable${isMobileScreen && !isMobileHeroAiTapEnabled ? " vip-hero-title--disabled" : ""}`}
                            onClick={handleHeroAnalysisOpen}
                            title={isMobileScreen && !isMobileHeroAiTapEnabled ? "AI media analysis unlocks after autoplay finishes" : "Click for AI media analysis"}
                            aria-disabled={isMobileScreen && !isMobileHeroAiTapEnabled}
                          >
                            {currentHeroTag && (
                              <EngagementBar
                                tag={currentHeroTag}
                                className="vip-hero-title__comment"
                              />
                            )}
                            <span className="vip-hero-title__text">
                              {currentHeroTag?.title}
                            </span>
                          </h2>
                        </div>
                      )}
                    </div>
                  );
                })()}

                <div ref={vipAfterHeroRef} className="vip-after-hero">
                  <div className="vip-after-hero-top-gap" aria-hidden="true" />
                  <div className="vip-hero-content vip-hero-content--desktop">
                    {!isMobileScreen && showHomepageAiHint && <HeroAiHint />}
                    <h2
                      ref={vipHeroDesktopTitleRef}
                      className="vip-hero-title vip-hero-title--clickable"
                      onClick={handleHeroAnalysisOpen}
                      title="Click for AI media analysis"
                    >
                      {currentHeroTag && (
                        <EngagementBar
                          tag={currentHeroTag}
                          className="vip-hero-title__comment vip-hero-title__comment--desktop"
                        />
                      )}
                      <span className="vip-hero-title__text">
                        {currentHeroTag?.title}
                      </span>
                    </h2>
                  </div>
                  {firstTrendingTag && (
                    <NewsRow
                      tag={firstTrendingTag}
                      tagIndex={0}
                    />
                  )}

                  {remainingTrendingTags.length > 0 && (
                    <div className="vip-more-rows">
                      {remainingTrendingTags.map((tag, index) => (
                        <React.Fragment key={index + 1}>
                          <NewsRow
                            tag={tag}
                            tagIndex={index + 1}
                          />
                        </React.Fragment>
                      ))}

                      {/* Bottom-of-feed indicator */}
                      {loadingMoreTrending && (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            padding: "24px 0 12px",
                            gap: 10,
                            opacity: 0.6,
                            color: "#aaa",
                            fontSize: 13,
                          }}
                        >
                          <div
                            style={{
                              width: 18,
                              height: 18,
                              border: "2px solid #555",
                              borderTopColor: "#aaa",
                              borderRadius: "50%",
                              animation: "spin 0.8s linear infinite",
                            }}
                          />
                          Loading more headlines…
                        </div>
                      )}
                      {!loadingMoreTrending && !hasMoreTrending && filteredTrendingTags.length > 0 && (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            padding: "20px 0 8px",
                            opacity: 0.3,
                            fontSize: 11,
                            color: "#888",
                            letterSpacing: "0.05em",
                            textTransform: "uppercase",
                          }}
                        >
                          ── all caught up ──
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
        </div>
      )}

      {/* ================= LIVE TV ================= */}
      {activeTab === "live_tv" && (
        <div className={`trending-scroll fancy-scroll new_livess ${playingLiveChannel ? 'has-player' : ''}`}>
          {/* Live TV Player Section - Embedded in layout */}
          {playingLiveChannel && (
            <div className="live-player-container">
              <div className="live-player-wrapper">
                <div className="live-player-inner">
                  {/* LiveStreamPlayer tries col F (YouTube) → G (IPTV) → E (Website) in order, falling back on error */}
                  <LiveStreamPlayer
                    videoUrls={playingLiveChannel.video_urls || []}
                    channelKey={playingLiveChannel.name + playingLiveChannel.country}
                  />
                </div>
                
                {/* Channel Info Overlay */}
                <div className="live-player-info">
                  <div className="d-flex justify-content-between align-items-center w-100">
                    <div>
                      <h5 className="m-0 text-white">{playingLiveChannel.name}</h5>
                      <p className="m-0 text-white-50" style={{ fontSize: "12px" }}>
                        {playingLiveChannel.country} · {playingLiveChannel.language}
                      </p>
                    </div>
                    {/* <button
                      className="btn btn-sm btn-outline-light rounded-circle"
                      style={{ width: "32px", height: "32px", padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }}
                      onClick={() => setPlayingLiveChannel(null)}
                    >
                      ×
                    </button> */}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Search channels section (disabled) ──
          <div className="d-flex gap-2 align-items-center mb-3 flex-wrap">
            <input
              className="form-control"
              style={{ maxWidth: 340 }}
              placeholder="Search channels…"
              value={liveSearch}
              onChange={(e) => setLiveSearch(e.target.value)}
            />

            <button
              className={`btn btn-sm ${liveFilterIptv ? "btn-warning" : "btn-outline-warning"
                }`}
              onClick={() => setLiveFilterIptv((v) => !v)}
              title="Only channels with an IPTV / m3u8 stream"
            >
              📡 IPTV
            </button>

            <button
              className={`btn btn-sm ${liveFilterYt ? "btn-danger" : "btn-outline-danger"
                }`}
              onClick={() => setLiveFilterYt((v) => !v)}
              title="Only channels with a YouTube live link"
            >
              ▶ YouTube
            </button>

            <button
              className="btn btn-sm btn-outline-light ms-auto"
              onClick={() =>
                fetchLiveChannels(
                  selectedCountry,
                  liveFilterIptv,
                  liveFilterYt,
                )
              }
              disabled={liveChannelsLoading}
            >
              ↺ Refresh
            </button>
          </div>

          {liveChannelsError && (
            <div className="alert alert-danger py-2">{liveChannelsError}</div>
          )}

          {liveChannelsLoading ? (
            <div className="text-center text-light py-5">
              Loading channels…
            </div>
          ) : (
            <div className="row g-2">
              {(liveChannels || [])
                .filter((c) => {
                  // Filter by search text
                  const name = (c?.name || "").toLowerCase();
                  const country = (c?.country || "").toLowerCase();
                  const language = (c?.language || "").toLowerCase();
                  const q = liveSearch.trim().toLowerCase();
                  const matchesSearch =
                    !q ||
                    name.includes(q) ||
                    country.includes(q) ||
                    language.includes(q);

                  // Filter by selected country
                  // LIVE_TV.csv uses full country names AND code sometimes, but frontend selectedCountry is a code.
                  // So we must lookup the full name from `countries` constant for robust filtering
                  const countryObj = countries.find(
                    (co) => co.code === selectedCountry,
                  );
                  const countryName = countryObj ? countryObj.name : "";

                  const countryMatch =
                    c.country_code === selectedCountry ||
                    c.country === selectedCountry ||
                    c.country === countryName ||
                    (c.country && c.country.includes(selectedCountry));

                  // The shared language selector also applies to Live outlets.
                  // Normalize common dataset aliases (e.g. Bengali/Bangla) and
                  // handle composite language cells such as English/French.
                  const languageMatch = liveChannelMatchesLanguage(c.language, selectedLanguage);

                  return matchesSearch && countryMatch && languageMatch;
                })
                .map((ch) => {
                  const logo = ch.website ? getFaviconLogo(ch.website) : null;
                  const iptvUrl = (ch.iptv_links || [])[0] || null;
                  const ytUrl = (ch.youtube_links || [])[0] || null;
                  // prefer IPTV for "Watch", fallback to YouTube, then website
                  const primaryUrl = iptvUrl || ytUrl || ch.website || null;

                  return (
                    <div
                      key={`${ch.name}-${ch.country_code}`}
                      className="col-12 col-md-6 col-lg-4"
                    >
                      <div
                        className="p-3 rounded h-100"
                        style={{
                          background: "rgba(255,255,255,0.06)",
                          border: "1px solid rgba(255,255,255,0.12)",
                          display: "flex",
                          flexDirection: "column",
                          gap: 8,
                        }}
                      >
                        <!-- Name + favicon -->
                        <div className="d-flex align-items-center gap-2">
                          {logo ? (
                            <img
                              src={logo}
                              alt=""
                              width={22}
                              height={22}
                              style={{ borderRadius: 4, flexShrink: 0 }}
                              onError={(e) =>
                                (e.currentTarget.style.display = "none")
                              }
                            />
                          ) : (
                            <div
                              style={{
                                width: 22,
                                height: 22,
                                borderRadius: 4,
                                background: "rgba(255,255,255,0.15)",
                                flexShrink: 0,
                              }}
                            />
                          )}
                          {(() => {
                            const cShort = ch.country_code || (ch.country ? ch.country.substring(0, 3).toUpperCase() : "");
                            const lShort = ch.language ? ch.language.substring(0, 2).toUpperCase() : "";
                            const meta = [cShort, lShort].filter(Boolean);
                            const displayTitle = meta.length ? `${ch.name} (${meta.join('/')})` : ch.name;
                            return (
                              <span
                                className="text-light fw-semibold text-truncate d-block"
                                style={{ fontSize: 11, maxWidth: "100%" }}
                                title={displayTitle}
                              >
                                {displayTitle}
                              </span>
                            );
                          })()}
                        </div>

                        <!-- Meta badges -->
                        <div
                          className="d-flex flex-wrap gap-1"
                          style={{ fontSize: 11 }}
                        >
                          {ch.country && (
                            <span
                              className="badge"
                              style={{
                                background: "#1e3a5f",
                                color: "#7dd3fc",
                              }}
                            >
                              🌍 {ch.country}
                            </span>
                          )}
                          {ch.language && (
                            <span
                              className="badge"
                              style={{
                                background: "#1c2b1e",
                                color: "#86efac",
                              }}
                            >
                              🗣 {ch.language.split(";")[0].trim()}
                            </span>
                          )}
                          {ch.categories && (
                            <span
                              className="badge"
                              style={{
                                background: "#2d1b4e",
                                color: "#c4b5fd",
                              }}
                            >
                              {ch.categories}
                            </span>
                          )}
                          {ch.network && (
                            <span
                              className="badge"
                              style={{
                                background: "#4f46e5",
                                color: "#e0e7ff",
                              }}
                            >
                              🏢 {ch.network}
                            </span>
                          )}
                          {ch.owners && (
                            <span
                              className="badge"
                              style={{
                                background: "#0891b2",
                                color: "#cffafe",
                              }}
                            >
                              👔 {ch.owners}
                            </span>
                          )}
                          {ch.StationID && (
                            <span
                              className="badge"
                              style={{
                                background: "#475569",
                                color: "#f8fafc",
                              }}
                            >
                              ID: {ch.StationID}
                            </span>
                          )}
                        </div>

                        <!-- Stream type indicators + action buttons -->
                        <div className="d-flex gap-2 flex-wrap mt-auto align-items-center">
                           <button
                              className="btn btn-sm btn-primary"
                              style={{ fontSize: 11, background: "#3b82f6", border: "none" }}
                              onClick={() => setPlayingLiveChannel(ch)}
                            >
                              📺 Watch
                            </button>
                          {iptvUrl && (
                            <a
                              href={iptvUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="btn btn-sm btn-outline-warning"
                              style={{ fontSize: 11 }}
                            >
                              📡 IPTV
                            </a>
                          )}
                          {ytUrl && (
                            <a
                              href={ytUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="btn btn-sm btn-outline-danger"
                              style={{ fontSize: 11 }}
                            >
                              ▶ YouTube
                            </a>
                          )}
                          {!iptvUrl && !ytUrl && ch.website && (
                            <a
                              href={ch.website}
                              target="_blank"
                              rel="noreferrer"
                              className="btn btn-sm btn-outline-light"
                              style={{ fontSize: 11 }}
                            >
                              🌐 Website
                            </a>
                          )}
                          {!primaryUrl && (
                            <span style={{ color: "#64748b", fontSize: 11 }}>
                              No stream
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}

              {liveChannels.length === 0 && !liveChannelsLoading && (
                <div className="text-center text-secondary py-5 col-12">
                  No channels found for this selection.
                </div>
              )}
            </div>
          )}
          */}
        </div>
      )}

      {/* ROUTE INFO ALERT */}
      {routeInfo && (
        <div className="alert alert-danger mt-3">
          <strong>Distance:</strong> {routeInfo.distanceKm} km
          <br />
          <strong>Driving Time:</strong> ~{routeInfo.drivingTime} min
          <br />
          <strong>Walking Time:</strong> ~{routeInfo.walkingTime} min
        </div>
      )}
    </div>
  );
}



export default App;
